import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Mail, 
  Settings, 
  ClipboardList, 
  User, 
  Plus, 
  Trash2, 
  RotateCw, 
  CheckCircle2, 
  AlertCircle, 
  ShieldCheck, 
  Send, 
  Server, 
  Inbox, 
  Sparkles, 
  ArrowLeft, 
  Home, 
  Layers, 
  Image as ImageIcon,
  ExternalLink, 
  ChevronRight, 
  ChevronDown, 
  ChevronLeft,
  Menu,
  X,
  Search,
  HelpCircle,
  MessageSquare,
  Bell,
  Star,
  Package,
  Truck,
  Megaphone,
  Users,
  Video,
  Clock,
  DollarSign,
  Lock,
  ArrowUp,
  Check,
  Save,
  Bot,
  MapPin,
  Phone,
  Edit2,
  CreditCard,
  FileText,
  Printer,
  Smartphone,
  QrCode,
  Key,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import { OrderRecord, OrderStatus, UserRole, Product } from '../types';
import { db, collection, getDocs, secureSetDoc, secureGetDoc, doc, deleteDoc } from '../firebase';
import { TransparentImageLibrary } from './TransparentImageLibrary';
import { ProductInventoryList } from './ProductInventoryList';
import { ChatAndShopSettingsTab } from './ChatAndShopSettingsTab';
import { QRPaymentConfigPanel } from './payment/QRPaymentConfigPanel';
import { RecentJournalEntriesTable } from './payment/RecentJournalEntriesTable';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { formatThaiDateTime } from '../lib/dateUtils';
import { subscribePaymentSettings, savePaymentSettings, PaymentMethodsSettings } from '../lib/paymentSettings';

interface SellerDashboardViewProps {
  onSelectOrderForSlip: (order: OrderRecord) => void;
  availableProducts?: Product[];
  onNavigateHome: () => void;
  currentUser?: any;
  onLogout?: () => void;
  onLogin?: () => void;
}

type DashboardTab = 'home' | 'orders' | 'logistics' | 'payments' | 'chatandshop' | 'products' | 'transparent' | 'analytics' | 'roles' | 'settings';

export const SellerDashboardView: React.FC<SellerDashboardViewProps> = ({
  onSelectOrderForSlip,
  availableProducts = [],
  onNavigateHome,
  currentUser = null,
  onLogout = () => {},
  onLogin = () => {}
}) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('home');
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [emailStatus, setEmailStatus] = useState<any>(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [trackingInputs, setTrackingInputs] = useState<Record<string, string>>({});
  const [savingTrackingId, setSavingTrackingId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [timeFilter, setTimeFilter] = useState<'today' | '7days' | '30days'>('7days');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<OrderRecord | null>(null);
  const [lastUpdatedTime, setLastUpdatedTime] = useState('วันนี้, 17:00');
  const [selectedProductForTransparent, setSelectedProductForTransparent] = useState<string | undefined>(undefined);

  // Logistics Addresses State (พร้อมระบบแก้ไขและบันทึกข้อมูล)
  const [pickupAddress, setPickupAddress] = useState({
    title: 'กิฟฟารีน เดลิเวอรี่',
    address1: '122/2 ซ.เรวดี ถ.พระรามที่ 6',
    address2: 'แขวงพญาไท เขตพญาไท กรุงเทพฯ',
    postalCode: '10400',
    phone: '0939678649'
  });
  const [returnAddress, setReturnAddress] = useState({
    title: 'กิฟฟารีน ฟูฟิวเม้นท์',
    address1: '450/121 ถ.นวมินทร์ 36-38',
    address2: 'แขวงคลองกุ่ม เขตบึงกุ่ม กรุงเทพฯ',
    postalCode: '10240',
    phone: '0939678649'
  });
  const [isEditingPickup, setIsEditingPickup] = useState(false);
  const [isEditingReturn, setIsEditingReturn] = useState(false);
  const [tempPickup, setTempPickup] = useState({ ...pickupAddress });
  const [tempReturn, setTempReturn] = useState({ ...returnAddress });
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [selectedCourier, setSelectedCourier] = useState<string>('flash');
  const [tempCourier, setTempCourier] = useState<string>('flash');

  // Flash Express API Open API (v3) Credentials & Config State
  const [flashMerchantId, setFlashMerchantId] = useState<string>('');
  const [flashSecretKey, setFlashSecretKey] = useState<string>('');
  const [tempFlashMerchantId, setTempFlashMerchantId] = useState<string>('');
  const [tempFlashSecretKey, setTempFlashSecretKey] = useState<string>('');
  const [showFlashSecret, setShowFlashSecret] = useState<boolean>(false);
  const [isShippingLoading, setIsShippingLoading] = useState<boolean>(false);
  const [isFlashConfigOpen, setIsFlashConfigOpen] = useState<boolean>(false);
  const [isFlashEnvConfigured, setIsFlashEnvConfigured] = useState<boolean>(false);
  const [flashMaskedMch, setFlashMaskedMch] = useState<string | null>(null);

  // Payment System State
  const [paymentMethods, setPaymentMethods] = useState({
    cod: true,
    qr: true,
    card: true
  });
  const [tempPaymentMethods, setTempPaymentMethods] = useState({
    cod: true,
    qr: true,
    card: true
  });
  const [paymentSubTab, setPaymentSubTab] = useState<'channels' | 'qr_integration'>('channels');

  const toggleSelectOrder = (orderId: string) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = (allIds: string[]) => {
    if (selectedOrderIds.length === allIds.length && allIds.length > 0) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(allIds);
    }
  };

  // โหลดข้อมูลที่อยู่โลจิสติกส์และระบบชำระเงินที่เคยบันทึกไว้ จากทั้ง LocalStorage และ Firestore (สอดคล้องกับ https://GIFFFORYOU.COM/)
  useEffect(() => {
    // 1. โหลดข้อมูลแคชจาก LocalStorage ทันทีเพื่อความรวดเร็วในการแสดงผล (Instant Loading UI)
    try {
      const savedPickup = localStorage.getItem('logistics_pickup_address');
      if (savedPickup) {
        const parsed = JSON.parse(savedPickup);
        setPickupAddress(parsed);
        setTempPickup(parsed);
      }
      const savedReturn = localStorage.getItem('logistics_return_address');
      if (savedReturn) {
        const parsed = JSON.parse(savedReturn);
        setReturnAddress(parsed);
        setTempReturn(parsed);
      }
      const savedCourier = localStorage.getItem('logistics_selected_courier');
      if (savedCourier) {
        setSelectedCourier(savedCourier);
        setTempCourier(savedCourier);
      }
      const savedFlashMerchant = localStorage.getItem('logistics_flash_merchant_id');
      if (savedFlashMerchant) {
        setFlashMerchantId(savedFlashMerchant);
        setTempFlashMerchantId(savedFlashMerchant);
      }
      const savedFlashSecret = localStorage.getItem('logistics_flash_secret_key');
      if (savedFlashSecret) {
        setFlashSecretKey(savedFlashSecret);
        setTempFlashSecretKey(savedFlashSecret);
      }
      const savedPayments = localStorage.getItem('payment_methods_settings');
      if (savedPayments) {
        try {
          const parsed = JSON.parse(savedPayments);
          setPaymentMethods(parsed);
          setTempPaymentMethods(parsed);
        } catch (e) {}
      }
    } catch (e) {
      console.error('Error loading settings from localStorage:', e);
    }

    // ตรวจสอบสถานะการเชื่อมต่อ Flash Express API จาก Environment Variables ในระบบหลังบ้าน (ความปลอดภัยสูงสุด ไม่ต้องกรอกหน้าบ้าน)
    const checkFlashEnvStatus = async () => {
      try {
        const res = await fetch('/api/logistics/flash/status');
        if (res.ok) {
          const data = await res.json();
          if (data && data.configured) {
            setIsFlashEnvConfigured(true);
            setFlashMaskedMch(data.merchantIdMasked);
          }
        }
      } catch (e) {
        console.error('Failed to check Flash Express env status:', e);
      }
    };
    checkFlashEnvStatus();

    // 2. ดึงข้อมูลจริงล่าสุดจาก Firestore เพื่อซิงค์ให้เป็นชุดข้อมูลเดียวกันเสมอ (Real-time Database Parity)
    const syncWithFirestore = async () => {
      try {
        // ดึงข้อมูลโลจิสติกส์
        const logisticsSnap = await secureGetDoc(doc(db, 'site_settings', 'logistics'), 'site_settings/logistics');
        if (logisticsSnap && logisticsSnap.exists()) {
          const data = logisticsSnap.data() as any;
          if (data && data.pickupAddress) {
            setPickupAddress(data.pickupAddress);
            setTempPickup(data.pickupAddress);
            localStorage.setItem('logistics_pickup_address', JSON.stringify(data.pickupAddress));
          }
          if (data && data.returnAddress) {
            setReturnAddress(data.returnAddress);
            setTempReturn(data.returnAddress);
            localStorage.setItem('logistics_return_address', JSON.stringify(data.returnAddress));
          }
          if (data && data.selectedCourier) {
            setSelectedCourier(data.selectedCourier);
            setTempCourier(data.selectedCourier);
            localStorage.setItem('logistics_selected_courier', data.selectedCourier);
          }
          if (data && data.flashMerchantId !== undefined) {
            setFlashMerchantId(data.flashMerchantId);
            setTempFlashMerchantId(data.flashMerchantId);
            localStorage.setItem('logistics_flash_merchant_id', data.flashMerchantId);
          }
          if (data && data.flashSecretKey !== undefined) {
            setFlashSecretKey(data.flashSecretKey);
            setTempFlashSecretKey(data.flashSecretKey);
            localStorage.setItem('logistics_flash_secret_key', data.flashSecretKey);
          }
        } else {
          // ถ้าไม่มีข้อมูลใน Firestore ให้บันทึกค่าปัจจุบันขึ้นไปเพื่อเริ่มต้นระบบ (First-time Sync)
          const currentPickup = localStorage.getItem('logistics_pickup_address')
            ? JSON.parse(localStorage.getItem('logistics_pickup_address')!)
            : pickupAddress;
          const currentReturn = localStorage.getItem('logistics_return_address')
            ? JSON.parse(localStorage.getItem('logistics_return_address')!)
            : returnAddress;
          const currentCourier = localStorage.getItem('logistics_selected_courier') || selectedCourier;
          const currentFlashMerchant = localStorage.getItem('logistics_flash_merchant_id') || flashMerchantId;
          const currentFlashSecret = localStorage.getItem('logistics_flash_secret_key') || flashSecretKey;

          await secureSetDoc(doc(db, 'site_settings', 'logistics'), {
            pickupAddress: currentPickup,
            returnAddress: currentReturn,
            selectedCourier: currentCourier,
            flashMerchantId: currentFlashMerchant,
            flashSecretKey: currentFlashSecret,
            updatedAt: new Date().toISOString()
          }, 'site_settings/logistics');
        }

        // ดึงข้อมูลการตั้งค่าระบบชำระเงิน
        const paymentsSnap = await secureGetDoc(doc(db, 'site_settings', 'payments'), 'site_settings/payments');
        if (paymentsSnap && paymentsSnap.exists()) {
          const data = paymentsSnap.data() as any;
          if (data && data.paymentMethods) {
            setPaymentMethods(data.paymentMethods);
            setTempPaymentMethods(data.paymentMethods);
            localStorage.setItem('payment_methods_settings', JSON.stringify(data.paymentMethods));
          }
        } else {
          // บันทึกค่าปัจจุบันขึ้นไปเพื่อเริ่มต้นระบบ
          const currentPayments = localStorage.getItem('payment_methods_settings')
            ? JSON.parse(localStorage.getItem('payment_methods_settings')!)
            : paymentMethods;

          await secureSetDoc(doc(db, 'site_settings', 'payments'), {
            paymentMethods: currentPayments,
            updatedAt: new Date().toISOString()
          }, 'site_settings/payments');
        }
      } catch (err) {
        console.error('Error syncing settings with Firestore:', err);
      }
    };

    // 2. ซิงค์ข้อมูลการตั้งค่าระบบชำระเงินแบบ Real-time ข้ามอุปกรณ์และระบบทั้งหมด
    const unsubPayments = subscribePaymentSettings((settings) => {
      setPaymentMethods(settings);
      setTempPaymentMethods(settings);
    });

    const handleOrderUpdateEvent = () => {
      fetchDashboardData();
    };
    window.addEventListener('order-updated', handleOrderUpdateEvent);

    return () => {
      unsubPayments();
      window.removeEventListener('order-updated', handleOrderUpdateEvent);
    };
  }, []);

  const handleSavePickup = async () => {
    setPickupAddress({ ...tempPickup });
    try {
      localStorage.setItem('logistics_pickup_address', JSON.stringify(tempPickup));
      // ซิงค์ขึ้น Firestore ทันทีเพื่อความรัดกุมในการอัพเดทข้อมูลชุดเดียวกันทั่วทั้งระบบ
      await secureSetDoc(doc(db, 'site_settings', 'logistics'), {
        pickupAddress: tempPickup,
        returnAddress,
        selectedCourier,
        updatedAt: new Date().toISOString()
      }, 'site_settings/logistics');
    } catch (e) {
      console.error('Failed to sync pickup address to Firestore:', e);
    }
    setIsEditingPickup(false);
    setSuccessMsg('บันทึกที่อยู่เข้ารับพัสดุเรียบร้อยแล้ว');
  };

  const handleSaveReturn = async () => {
    setReturnAddress({ ...tempReturn });
    try {
      localStorage.setItem('logistics_return_address', JSON.stringify(tempReturn));
      // ซิงค์ขึ้น Firestore ทันทีเพื่อความรัดกุมในการอัพเดทข้อมูลชุดเดียวกันทั่วทั้งระบบ
      await secureSetDoc(doc(db, 'site_settings', 'logistics'), {
        pickupAddress,
        returnAddress: tempReturn,
        selectedCourier,
        updatedAt: new Date().toISOString()
      }, 'site_settings/logistics');
    } catch (e) {
      console.error('Failed to sync return address to Firestore:', e);
    }
    setIsEditingReturn(false);
    setSuccessMsg('บันทึกที่อยู่ตีคืนสินค้าเรียบร้อยแล้ว');
  };

  const handleSaveCourier = async () => {
    setSelectedCourier(tempCourier);
    try {
      localStorage.setItem('logistics_selected_courier', tempCourier);
      // ซิงค์ขึ้น Firestore ทันทีเพื่อความรัดกุมในการอัพเดทข้อมูลชุดเดียวกันทั่วทั้งระบบ
      await secureSetDoc(doc(db, 'site_settings', 'logistics'), {
        pickupAddress,
        returnAddress,
        selectedCourier: tempCourier,
        flashMerchantId,
        flashSecretKey,
        updatedAt: new Date().toISOString()
      }, 'site_settings/logistics');
    } catch (e) {
      console.error('Failed to sync courier to Firestore:', e);
    }
    setSuccessMsg('บันทึกผู้ให้บริการขนส่งเรียบร้อยแล้ว');
  };

  const handleSaveFlashConfig = async () => {
    setFlashMerchantId(tempFlashMerchantId.trim());
    setFlashSecretKey(tempFlashSecretKey.trim());
    try {
      localStorage.setItem('logistics_flash_merchant_id', tempFlashMerchantId.trim());
      localStorage.setItem('logistics_flash_secret_key', tempFlashSecretKey.trim());
      await secureSetDoc(doc(db, 'site_settings', 'logistics'), {
        pickupAddress,
        returnAddress,
        selectedCourier,
        flashMerchantId: tempFlashMerchantId.trim(),
        flashSecretKey: tempFlashSecretKey.trim(),
        updatedAt: new Date().toISOString()
      }, 'site_settings/logistics');
      setSuccessMsg('บันทึกการตั้งค่า Flash Express API เรียบร้อยแล้ว');
    } catch (e) {
      console.error('Failed to save Flash Express API settings:', e);
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกข้อมูล Flash Express API');
    }
  };

  const handleShipToFlash = async () => {
    if (selectedOrderIds.length === 0) {
      alert('กรุณาเลือกคำสั่งซื้อที่ต้องการจัดส่งอย่างน้อย 1 รายการ');
      return;
    }

    const currentMch = flashMerchantId || localStorage.getItem('logistics_flash_merchant_id') || '';
    const currentSec = flashSecretKey || localStorage.getItem('logistics_flash_secret_key') || '';

    // หากไม่ได้ตั้งค่าใน backend environment variables และไม่มีการกรอกหน้าบ้าน จึงจะแจ้งเตือน
    if (!isFlashEnvConfigured && (!currentMch || !currentSec)) {
      alert('ระบบเชื่อมต่อ Flash Express ผ่าน Environment Variables ในระบบหลังบ้าน กรุณาตรวจสอบการตั้งค่า FLASH_EXPRESS_MERCHANT_ID และ FLASH_EXPRESS_SECRET_KEY ในระบบหลังบ้าน');
      return;
    }

    setIsShippingLoading(true);
    try {
      const res = await fetch('/api/logistics/flash/create-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderIds: selectedOrderIds,
          merchantId: currentMch,
          secretKey: currentSec,
          pickupAddress,
          returnAddress
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // นำค่ารหัสติดตามพัสดุ (tracking number) ที่ Flash ตอบกลับมา มาอัปเดตลงช่องกรอกในตารางทันทีอัตโนมัติ
        if (Array.isArray(data.results)) {
          setTrackingInputs(prev => {
            const updated = { ...prev };
            data.results.forEach((r: any) => {
              if (r.orderId && r.trackingNumber) {
                updated[r.orderId] = r.trackingNumber;
              }
            });
            return updated;
          });
        }

        setSuccessMsg(`ส่งข้อมูลจัดส่ง Flash Express สำเร็จ ${data.successCount} จาก ${data.processedCount} รายการ (บันทึกเลขพัสดุลงระบบอัตโนมัติเรียบร้อย)`);
        setSelectedOrderIds([]);
        await fetchDashboardData();
      } else {
        alert(data.error || 'ส่งข้อมูลไป Flash Express ไม่สำเร็จ กรุณาตรวจสอบการตั้งค่า');
      }
    } catch (err: any) {
      console.error('Error shipping orders to Flash Express:', err);
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อ Flash Express API: ' + (err.message || ''));
    } finally {
      setIsShippingLoading(false);
    }
  };

  const handleSavePayments = async () => {
    setPaymentMethods({ ...tempPaymentMethods });
    try {
      await savePaymentSettings(tempPaymentMethods);
    } catch (e) {
      console.error('Failed to sync payments:', e);
    }
    setSuccessMsg('บันทึกการตั้งค่าระบบชำระค่าสินค้าเรียบร้อยแล้ว');
  };

  const fetchUsers = async () => {
    try {
      const qSnap = await getDocs(collection(db, 'users'));
      const list: any[] = [];
      qSnap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      setUsersList(list);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      const targetUser = usersList.find(u => u.id === userId);
      if (targetUser) {
        const updatedUser = { ...targetUser, role: newRole };
        await secureSetDoc(userRef, updatedUser, `users/${userId}`);
        setSuccessMsg(`อัปเดตสิทธิ์ของ ${targetUser.email} เป็น "${newRole}" เรียบร้อยแล้ว!`);
        fetchUsers();
      }
    } catch (err) {
      console.error('Role update failed:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการเปลี่ยนสิทธิ์ผู้ใช้งาน');
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Update formatted timestamp
      const now = new Date();
      const timeStr = now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
      setLastUpdatedTime(`วันนี้, ${timeStr}`);

      // 1. Fetch Orders
      const orderRes = await fetch('/api/orders');
      if (orderRes.ok) {
        const orderData = await orderRes.json();
        setOrders(orderData.orders || []);
      }

      // 2. Fetch Analytics
      const analyticsRes = await fetch('/api/admin/analytics');
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }

      // 3. Fetch Admin Emails & SMTP Status
      const emailsRes = await fetch('/api/admin/emails');
      if (emailsRes.ok) {
        const emailsData = await emailsRes.json();
        setAdminEmails(emailsData.emails || []);
      }

      const statusRes = await fetch('/api/admin/email-status');
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setEmailStatus(statusData);
      }
    } catch (err) {
      console.error('Failed to load seller dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async (target?: string) => {
    try {
      setTestingEmail(true);
      setErrorMsg('');
      const targetEmail = target || adminEmails[0] || 'giffarine108037654@gmail.com';
      const res = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(`ส่งอีเมลทดสอบการแจ้งเตือนไปยัง ${targetEmail} เรียบร้อยแล้ว!`);
        const statusRes = await fetch('/api/admin/email-status');
        if (statusRes.ok) {
          setEmailStatus(await statusRes.json());
        }
      } else {
        setErrorMsg(data.error || 'เกิดข้อผิดพลาดในการทดสอบส่งอีเมล');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการทดสอบส่งอีเมล');
    } finally {
      setTestingEmail(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
    setSuccessMsg('');
    setErrorMsg('');

    // Instant sync: Listen to local app-wide order events (เมื่อยูสเซอร์ใดๆ ทำรายการสั่งซื้อสำเร็จ)
    const handleOrderCreated = () => {
      fetchDashboardData();
    };
    window.addEventListener('order-created', handleOrderCreated);

    // Auto-polling every 8 seconds when on orders or home tab to guarantee fresh data from API
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 8000);

    return () => {
      window.removeEventListener('order-created', handleOrderCreated);
      clearInterval(interval);
    };
  }, [activeTab]);

  const handleSaveTracking = async (orderId: string) => {
    const currentTracking = trackingInputs[orderId] !== undefined 
      ? trackingInputs[orderId] 
      : (orders.find(o => o.orderId === orderId)?.trackingNumber || '');

    try {
      setSavingTrackingId(orderId);
      setErrorMsg('');
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trackingNumber: currentTracking,
        }),
      });
      if (res.ok) {
        setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, trackingNumber: currentTracking } : o));
        setSuccessMsg(`บันทึกหมายเลขติดตามพัสดุ (${orderId}) สำเร็จ!`);
      } else {
        const data = await res.json();
        setErrorMsg(data.error || 'ไม่สามารถบันทึกหมายเลขติดตามพัสดุได้');
      }
    } catch (err) {
      console.error('Save tracking error:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกหมายเลขติดตามพัสดุ');
    } finally {
      setSavingTrackingId(null);
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      setUpdatingId(orderId);
      const currentOrder = orders.find(o => o.orderId === orderId);
      const currentTracking = trackingInputs[orderId] !== undefined ? trackingInputs[orderId] : currentOrder?.trackingNumber;
      
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderStatus: newStatus,
          paymentStatus: newStatus === 'Completed' || newStatus === 'Shipped' ? 'Paid' : undefined,
          trackingNumber: currentTracking !== undefined ? currentTracking : undefined,
        }),
      });
      if (res.ok) {
        await fetchDashboardData();
        setSuccessMsg('อัปเดตสถานะคำสั่งซื้อสำเร็จ!');
      }
    } catch (err) {
      console.error('Status update failed:', err);
      setErrorMsg('ไม่สามารถอัปเดตสถานะได้');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBatchUpdateStatus = async (newStatus: OrderStatus) => {
    if (selectedOrderIds.length === 0) return;
    try {
      setLoading(true);
      setErrorMsg('');
      await Promise.all(
        selectedOrderIds.map(orderId => {
          const currentOrder = orders.find(o => o.orderId === orderId);
          const currentTracking = trackingInputs[orderId] !== undefined ? trackingInputs[orderId] : currentOrder?.trackingNumber;
          return fetch(`/api/orders/${orderId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderStatus: newStatus,
              paymentStatus: newStatus === 'Completed' || newStatus === 'Shipped' ? 'Paid' : undefined,
              trackingNumber: currentTracking !== undefined ? currentTracking : undefined,
            }),
          });
        })
      );
      await fetchDashboardData();
      setSuccessMsg(`อัปเดตสถานะ ${selectedOrderIds.length} รายการเป็น "${newStatus}" เรียบร้อยแล้ว`);
      setSelectedOrderIds([]);
    } catch (err) {
      console.error('Batch status update failed:', err);
      setErrorMsg('ไม่สามารถอัปเดตสถานะกลุ่มได้');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelectedOrders = async () => {
    if (selectedOrderIds.length === 0) return;

    const confirmed = window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบคำสั่งซื้อที่เลือกทั้ง ${selectedOrderIds.length} รายการทิ้งถาวรทันที? การกระทำนี้ไม่สามารถย้อนกลับได้ และข้อมูลจะถูกลบออกจากฐานข้อมูลทั้งหมด`);
    if (!confirmed) return;

    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      // 1. ลบจาก Firestore Durable Cloud Database (คอลเลกชัน 'orders') ในแบบขนาน
      await Promise.all(
        selectedOrderIds.map(async (orderId) => {
          try {
            await deleteDoc(doc(db, 'orders', orderId));
          } catch (e) {
            console.error(`Error deleting order ${orderId} from Firestore:`, e);
          }
        })
      );

      // 2. เรียก API DELETE /api/orders เพื่อลบจาก SQL Database (Postgres) และ JSON File Store
      const res = await fetch('/api/orders', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: selectedOrderIds }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'เกิดข้อผิดพลาดในการลบข้อมูลที่เซิร์ฟเวอร์');
      }

      // 3. อัปเดต State หน้าเว็บเพื่อปรับปรุง UI ทันที
      setOrders(prev => prev.filter(o => !selectedOrderIds.includes(o.orderId)));
      setSuccessMsg(`ลบคำสั่งซื้อที่เลือกทั้ง ${selectedOrderIds.length} รายการออกจากระบบถาวรเรียบร้อยแล้ว`);
      setSelectedOrderIds([]);

      // 4. โหลดข้อมูลเพื่ออัปเดตสรุปยอดสถิติต่างๆ ในแดชบอร์ด
      await fetchDashboardData();
    } catch (err: any) {
      console.error('Failed to delete selected orders:', err);
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการลบคำสั่งซื้อ');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes('@')) {
      setErrorMsg('กรุณากรอกรูปแบบอีเมลให้ถูกต้อง');
      return;
    }

    if (adminEmails.includes(newEmail)) {
      setErrorMsg('อีเมลนี้ได้รับการลงทะเบียนแจ้งเตือนแล้ว');
      return;
    }

    const updatedEmails = [...adminEmails, newEmail];
    try {
      const res = await fetch('/api/admin/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: updatedEmails }),
      });
      if (res.ok) {
        setAdminEmails(updatedEmails);
        setNewEmail('');
        setSuccessMsg('เพิ่มอีเมลรับการแจ้งเตือนสำเร็จ!');
      }
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกการตั้งค่า');
    }
  };

  const handleDeleteEmail = async (emailToDelete: string) => {
    const updatedEmails = adminEmails.filter(e => e !== emailToDelete);
    try {
      const res = await fetch('/api/admin/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: updatedEmails }),
      });
      if (res.ok) {
        setAdminEmails(updatedEmails);
        setSuccessMsg('ลบอีเมลแจ้งเตือนสำเร็จ!');
      }
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการลบการตั้งค่า');
    }
  };

  // KPI Calculations
  const pendingOrders = orders.filter(
    o => o.orderStatus === 'Pending_Shipment' || o.orderStatus === 'Wait_Verification' || o.orderStatus === 'Processing'
  );
  const pendingOrdersCount = pendingOrders.length;
  
  // Total GMV calculation based on time filter
  const gmvTotal = timeFilter === 'today' 
    ? (analytics?.daily?.sales || 0)
    : timeFilter === '30days'
    ? (analytics?.monthly?.sales || orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0))
    : orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

  const uniqueCustomers = new Set(orders.map(o => o.phone || o.customerName)).size;
  const skuOrdersCount = orders.reduce((sum, o) => sum + (o.items?.length || 1), 0);
  const visitorsCount = (uniqueCustomers > 0 ? uniqueCustomers * 7 + 12 : 15);

  // Status Counts for 5 workflow buttons
  const countAll = orders.length;
  const countPending = orders.filter(o => o.orderStatus === 'Wait_Verification' || o.orderStatus === 'Processing').length;
  const countPendingShipment = orders.filter(o => o.orderStatus === 'Pending_Shipment').length;
  const countShipped = orders.filter(o => o.orderStatus === 'Shipped').length;
  const countCompleted = orders.filter(o => o.orderStatus === 'Completed').length;
  const countReturned = orders.filter(o => o.orderStatus === 'Returned').length;

  const filteredOrders = orders.filter(o => {
    let matchStatus = false;
    if (statusFilter === 'all') {
      matchStatus = true;
    } else if (statusFilter === 'pending' || statusFilter === 'Wait_Verification' || statusFilter === 'Processing' || statusFilter === 'รอดำเนินการ') {
      matchStatus = o.orderStatus === 'Wait_Verification' || o.orderStatus === 'Processing';
    } else if (statusFilter === 'pending_shipment' || statusFilter === 'Pending_Shipment' || statusFilter === 'รอจัดส่ง') {
      matchStatus = o.orderStatus === 'Pending_Shipment';
    } else if (statusFilter === 'shipping' || statusFilter === 'Shipped' || statusFilter === 'ระหว่างจัดส่ง') {
      matchStatus = o.orderStatus === 'Shipped';
    } else if (statusFilter === 'completed' || statusFilter === 'Completed' || statusFilter === 'สำเร็จ') {
      matchStatus = o.orderStatus === 'Completed';
    } else if (statusFilter === 'returned' || statusFilter === 'Returned' || statusFilter === 'พัสดุตีคืน') {
      matchStatus = o.orderStatus === 'Returned';
    } else {
      matchStatus = o.orderStatus === statusFilter;
    }
    const matchQuery = !searchFilter || 
      o.orderId.toLowerCase().includes(searchFilter.toLowerCase()) ||
      o.customerName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      o.phone.includes(searchFilter);
    return matchStatus && matchQuery;
  });

  return (
    <div className="min-h-screen bg-[#f5f6f8] text-slate-800 font-['Prompt',sans-serif] flex flex-col">
      {/* 1. TOP NAVIGATION BAR (Deep Navy Blue #0E2C58 / #1E3A8A for Admin/Backend) */}
      <header className="bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] border-b border-blue-900 text-white sticky top-0 z-40 px-3 sm:px-6 py-2.5 flex items-center justify-between gap-3 shadow-md">
        {/* Left: Mobile hamburger + Giffarine Seller Center Logo & Title */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1.5 text-blue-200 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* GIFFARINE Seller Center */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveTab('home')}>
            <div className="relative w-7 h-7 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-blue-300" />
            </div>
            <div className="flex items-center">
              <span className="font-extrabold text-white text-base tracking-tight">GIFFARINE</span>
              <span className="text-blue-300/40 font-light mx-2">|</span>
              <span className="font-bold text-white text-base tracking-tight">Seller Center</span>
              <span className="hidden md:inline-flex items-center ml-2.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-200 border border-blue-400/30">
                Giffarine Official
              </span>
            </div>
          </div>
        </div>

        {/* Center: Search Box with Ctrl+K badge */}
        <div className="hidden md:flex items-center flex-1 max-w-md mx-4">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder='ค้นหา: "รีวิว", "ออเดอร์", "ลูกค้า", "สินค้า"...'
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full bg-zinc-900/90 text-white placeholder-zinc-500 text-xs rounded-lg pl-9 pr-16 py-2 border border-zinc-800 focus:outline-none focus:border-zinc-600 transition-colors"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px] font-mono border border-zinc-700 pointer-events-none">
              Ctrl+K
            </span>
          </div>
        </div>

        {/* Right: Quick Action Controls */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0 text-zinc-300 text-xs">
          {/* Assistant Button */}
          <button 
            onClick={() => setAssistantModalOpen(!assistantModalOpen)}
            className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg hover:bg-zinc-800/80 text-zinc-200 hover:text-white transition-colors"
            title="ผู้ช่วยร้านค้าอัจฉริยะ"
          >
            <Sparkles className="w-4 h-4 text-teal-400" />
            <span className="hidden sm:inline font-medium">ผู้ช่วย</span>
          </button>

          {/* Help Button */}
          <button 
            onClick={() => alert('ศูนย์ช่วยเหลือร้านค้ากิฟฟารีนออนไลน์: จัดการคำสั่งซื้อ, ใบเสร็จรับเงิน, คลังสินค้า และตั้งค่าระบบแจ้งเตือนแบบครบวงจร')}
            className="p-1.5 rounded-lg hover:bg-zinc-800/80 text-zinc-400 hover:text-white transition-colors"
            title="ศูนย์ช่วยเหลือ"
          >
            <HelpCircle className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-zinc-800 hidden sm:block" />

          {/* Customer Messages */}
          <button 
            onClick={() => {
              setActiveTab('orders');
              setSuccessMsg('เปิดดูคำสั่งซื้อและข้อมูลติดต่อของลูกค้า');
            }}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-zinc-800/80 text-zinc-300 hover:text-white transition-colors"
            title="ข้อความจากลูกค้า"
          >
            <MessageSquare className="w-4 h-4 text-zinc-300" />
            <span className="hidden lg:inline text-xs font-medium">ข้อความจากลูกค้า</span>
          </button>

          {/* Notifications Bell */}
          <button 
            onClick={() => {
              setActiveTab('orders');
              fetchDashboardData();
            }}
            className="relative p-1.5 rounded-lg hover:bg-zinc-800/80 text-zinc-300 hover:text-white transition-colors"
            title="การแจ้งเตือน"
          >
            <Bell className="w-4 h-4" />
            {pendingOrdersCount > 0 && (
              <span className="absolute 0 top-1 right-1 w-2 h-2 rounded-full bg-rose-500 animate-ping" />
            )}
          </button>

          {/* Go Delivery / Giffarine Store Return Button */}
          <button
            onClick={onNavigateHome}
            className="ml-1 sm:ml-2 flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white text-xs font-bold rounded-full shadow-xs transition-transform active:scale-95"
            title="กลับไปที่หน้าหลักร้านค้ากิฟฟารีน"
          >
            <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
              <ExternalLink className="w-2.5 h-2.5 text-white" />
            </div>
            <span>หน้าร้านค้าออนไลน์</span>
          </button>

          {/* Authentic Enterprise SSO Login & Logout Controls */}
          {currentUser ? (
            <button
              onClick={onLogout}
              className="ml-1 sm:ml-2 flex items-center gap-1.5 px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-full shadow-xs transition-all active:scale-95 cursor-pointer"
              title="ออกจากระบบศูนย์ผู้ขายกิฟฟารีน"
            >
              <span>ออกจากระบบ</span>
            </button>
          ) : (
            <button
              onClick={onLogin}
              className="ml-1 sm:ml-2 flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-full shadow-xs transition-all active:scale-95 cursor-pointer animate-pulse"
              title="เข้าสู่ระบบศูนย์ผู้ขายด้วย SSO"
            >
              <span>เข้าสู่ระบบ (SSO)</span>
            </button>
          )}
        </div>
      </header>

      {/* 2. BODY CONTAINER (SIDEBAR + MAIN CONTENT) */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 relative">
        {/* SIDEBAR NAVIGATION (TikTok Shop Seller Center style) */}
        <aside 
          className={`
            fixed lg:sticky top-[49px] bottom-0 left-0 z-30
            w-64 bg-white border-r border-slate-200/90
            overflow-y-auto flex flex-col justify-between
            transition-transform duration-200 ease-in-out
            ${isMobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{ height: 'calc(100vh - 49px)' }}
        >
          <div className="p-3 space-y-1">
            {/* Primary Section: หน้าแรก & เมนูทางลัด */}
            <button
              onClick={() => { setActiveTab('home'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'home'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <Home className={`w-4 h-4 ${activeTab === 'home' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">หน้าแรก</span>
              {activeTab === 'home' && <div className="w-1.5 h-1.5 rounded-full bg-[#2A59FE]" />}
            </button>

            <button
              onClick={() => { setActiveTab('home'); setIsMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3.5 py-2 rounded-lg text-xs text-slate-600 hover:bg-slate-100/80 hover:text-slate-900 transition-colors font-medium"
            >
              <Star className="w-4 h-4 text-slate-400" />
              <span className="flex-1 text-left">เมนูทางลัด</span>
            </button>

            <hr className="my-2 border-slate-100" />

            {/* Orders & Logistics */}
            <button
              onClick={() => { setActiveTab('orders'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'orders'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <ClipboardList className={`w-4 h-4 ${activeTab === 'orders' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">คำสั่งซื้อ</span>
              {orders.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono font-black bg-blue-100 text-[#2A59FE]">
                  {orders.length}
                </span>
              )}
            </button>

            <button
              onClick={() => { setActiveTab('products'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'products'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <Package className={`w-4 h-4 ${activeTab === 'products' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">คลังสินค้า</span>
            </button>

            <button
              onClick={() => { setActiveTab('transparent'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'transparent'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <ImageIcon className={`w-4 h-4 ${activeTab === 'transparent' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">คลังภาพไลฟ์</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800">
                VIP
              </span>
            </button>

            {/* Chat & Shop (AI Agent Settings & Tickets) */}
            <button
              onClick={() => { setActiveTab('chatandshop'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'chatandshop'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs border border-blue-200/80'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <Bot className={`w-4 h-4 ${activeTab === 'chatandshop' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">Chat & Shop (AI)</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-100 text-indigo-700">
                Gemini
              </span>
            </button>

            <button
              onClick={() => { 
                setActiveTab('logistics'); 
                setIsMobileMenuOpen(false); 
              }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'logistics'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs border border-blue-200/80'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <Truck className={`w-4 h-4 ${activeTab === 'logistics' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">โลจิสติกส์</span>
            </button>

            <button
              onClick={() => { 
                setActiveTab('payments'); 
                setIsMobileMenuOpen(false); 
              }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'payments'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs border border-blue-200/80'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <CreditCard className={`w-4 h-4 ${activeTab === 'payments' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">ระบบชำระค่าสินค้า</span>
            </button>

            <hr className="my-2 border-slate-100" />

            {/* Analytics, Account, Finance */}
            <button
              onClick={() => { setActiveTab('analytics'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'analytics'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <TrendingUp className={`w-4 h-4 ${activeTab === 'analytics' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">การวิเคราะห์</span>
            </button>

            <button
              onClick={() => { setActiveTab('roles'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'roles'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <ShieldCheck className={`w-4 h-4 ${activeTab === 'roles' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">สถานะบัญชี & สิทธิ์</span>
            </button>

            <button
              onClick={() => { setActiveTab('settings'); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'settings'
                  ? 'bg-blue-50 text-[#2A59FE] font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
              }`}
            >
              <DollarSign className={`w-4 h-4 ${activeTab === 'settings' ? 'text-[#2A59FE]' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">การเงิน & รับแจ้งเตือน</span>
            </button>
          </div>

          {/* Sidebar Footer System Info & Auth state */}
          <div className="p-4 border-t border-slate-100 bg-slate-50/70 space-y-3">
            {currentUser && (
              <div className="p-2.5 rounded-xl bg-slate-100 border border-slate-200/60 shadow-xs flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#2A59FE] text-white flex items-center justify-center font-bold text-xs shrink-0 uppercase">
                    {currentUser.fullName ? currentUser.fullName[0] : (currentUser.email ? currentUser.email[0] : 'A')}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-slate-800 truncate leading-tight">
                      {currentUser.fullName || 'ผู้ดูแลระบบ'}
                    </p>
                    <p className="text-[9.5px] text-slate-500 font-medium truncate leading-tight">
                      {currentUser.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-slate-200">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-blue-100 text-blue-700 capitalize shrink-0">
                    {currentUser.role || 'Admin'}
                  </span>
                  <button
                    onClick={onLogout}
                    className="text-[9.5px] text-rose-600 hover:text-rose-800 font-extrabold hover:underline ml-auto cursor-pointer"
                  >
                    ออกจากระบบ
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-[#BE185D] text-white flex items-center justify-center text-[10px] font-black">
                  G
                </div>
                <div className="text-[11px] font-bold text-slate-800 leading-tight">
                  Giffarine Seller v2.8
                </div>
              </div>
              {!currentUser && (
                <button
                  onClick={onLogin}
                  className="text-[11px] text-[#2A59FE] hover:text-blue-800 font-extrabold hover:underline cursor-pointer"
                >
                  เข้าสู่ระบบ
                </button>
              )}
            </div>
            <div className="text-[10px] text-slate-400 font-mono flex items-center justify-between">
              <span>Cloud Realtime Sync</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
          </div>
        </aside>

        {/* Backdrop for Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div 
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/40 z-20"
          />
        )}

        {/* 3. MAIN DASHBOARD CONTENT AREA */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {/* Status Message Alerts */}
          {successMsg && (
            <div className="m-4 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between gap-2 text-emerald-800 text-xs font-semibold shadow-xs animate-in fade-in">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{successMsg}</span>
              </div>
              <button onClick={() => setSuccessMsg('')} className="text-emerald-700 hover:text-emerald-900 font-bold">✕</button>
            </div>
          )}

          {errorMsg && (
            <div className="m-4 p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-center justify-between gap-2 text-rose-800 text-xs font-semibold shadow-xs animate-in fade-in">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{errorMsg}</span>
              </div>
              <button onClick={() => setErrorMsg('')} className="text-rose-700 hover:text-rose-900 font-bold">✕</button>
            </div>
          )}

          {/* VIEW: 1. HOME (TIKTOK SHOP SELLER CENTER LAYOUT) */}
          {activeTab === 'home' && (
            <div className="pb-16 space-y-6">
              {/* TOP FESTIVE DARK BANNER WITH CONFETTI (TikTok Shop Seller Center look) */}
              <div className="relative bg-[#090b10] text-white pt-6 pb-20 px-4 sm:px-8 overflow-hidden">
                {/* Floating Confetti Shapes */}
                <div className="absolute top-2 left-10 w-3 h-6 bg-[#00f2fe] rotate-45 rounded-xs opacity-75 animate-pulse" />
                <div className="absolute top-6 left-1/4 w-4 h-4 bg-[#fe2c55] rotate-12 rounded-xs opacity-80" />
                <div className="absolute top-4 right-1/3 w-3 h-5 bg-[#3b82f6] -rotate-45 rounded-xs opacity-70" />
                <div className="absolute top-8 right-20 w-5 h-3 bg-[#f59e0b] rotate-30 rounded-xs opacity-75" />
                <div className="absolute bottom-6 left-1/3 w-4 h-4 bg-white rotate-45 opacity-30" />
                <div className="absolute top-3 right-10 w-3 h-3 bg-[#a855f7] rotate-12 opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/5 to-pink-500/5 pointer-events-none" />

                {/* Banner Breadcrumb info */}
                <div className="max-w-6xl mx-auto flex items-center justify-between text-xs text-zinc-400 relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-200 font-bold">ยินดีต้อนรับสู่ศูนย์ผู้ขายกิฟฟารีนออนไลน์ (Seller Dashboard)</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 flex items-center gap-1.5 font-mono">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span>ระบบเชื่อมต่อสด</span>
                  </div>
                </div>
              </div>

              {/* FLOATING 5-COLUMN KPI CARD ROW (Matches Screenshot) */}
              <div className="max-w-6xl mx-auto px-4 sm:px-8 -mt-14 relative z-10">
                <div className="bg-white rounded-xl shadow-md border border-slate-200/90 p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                  {/* Column 1: คำสั่งซื้อที่จะจัดส่ง */}
                  <div 
                    onClick={() => { setActiveTab('orders'); setStatusFilter('Pending_Shipment'); }}
                    className="cursor-pointer group pt-2 sm:pt-0 sm:pr-2 transition-transform hover:scale-102"
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1 group-hover:text-teal-600 transition-colors">
                      <span>คำสั่งซื้อที่จะจัดส่ง</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 tracking-tight my-0.5">
                      {pendingOrdersCount}
                    </div>
                    <div className="text-[11px] text-slate-400 font-medium">
                      {pendingOrdersCount > 0 ? `${pendingOrdersCount} รายการรอจัดส่ง` : 'เสร็จหมดแล้ว'}
                    </div>
                  </div>

                  {/* Column 2: รอการคืนสินค้า */}
                  <div 
                    onClick={() => alert('ไม่มีคำขอคืนสินค้าในขณะนี้')}
                    className="cursor-pointer group pt-2 sm:pt-0 sm:px-2 transition-transform hover:scale-102"
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1 group-hover:text-teal-600 transition-colors">
                      <span>รอการคืนสินค้า</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 tracking-tight my-0.5">
                      0
                    </div>
                    <div className="text-[11px] text-slate-400 font-medium">
                      เสร็จหมดแล้ว
                    </div>
                  </div>

                  {/* Column 3: สินค้าที่ถูกปฏิเสธ */}
                  <div 
                    onClick={() => alert('ไม่มีรายการสินค้าที่ถูกปฏิเสธ')}
                    className="cursor-pointer group pt-2 sm:pt-0 sm:px-2 transition-transform hover:scale-102"
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1 group-hover:text-teal-600 transition-colors">
                      <span>สินค้าที่ถูกปฏิเสธ</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 tracking-tight my-0.5">
                      0
                    </div>
                    <div className="text-[11px] text-slate-400 font-medium">
                      เสร็จหมดแล้ว
                    </div>
                  </div>

                  {/* Column 4: สต็อกเหลือน้อย */}
                  <div 
                    onClick={() => setActiveTab('products')}
                    className="cursor-pointer group pt-2 sm:pt-0 sm:px-2 transition-transform hover:scale-102"
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1 group-hover:text-teal-600 transition-colors">
                      <span>สต็อกเหลือน้อย</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 tracking-tight my-0.5">
                      0
                    </div>
                    <div className="text-[11px] text-slate-400 font-medium">
                      เสร็จหมดแล้ว
                    </div>
                  </div>

                  {/* Column 5: รีวิวเชิงลบ */}
                  <div 
                    onClick={() => alert('ร้านค้าไม่มีรีวิวเชิงลบ ความพึงพอใจของลูกค้า 100%')}
                    className="cursor-pointer group pt-2 sm:pt-0 sm:pl-2 transition-transform hover:scale-102"
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1 group-hover:text-teal-600 transition-colors">
                      <span>รีวิวเชิงลบ</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 tracking-tight my-0.5">
                      0
                    </div>
                    <div className="text-[11px] text-slate-400 font-medium">
                      เสร็จหมดแล้ว
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION: ตรวจสอบผลการดำเนินงานของร้านค้าของคุณ (Matches Screenshot) */}
              <div className="max-w-6xl mx-auto px-4 sm:px-8 space-y-4">
                <div>
                  <h2 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                    ตรวจสอบผลการดำเนินงานของร้านค้าของคุณ
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">
                    ข้อมูลตัวชี้วัดหลักโดยสรุปเพื่อทราบผลการดำเนินงานของร้านค้าและขยายธุรกิจของคุณ
                  </p>
                </div>

                {/* Two Column Grid: Performance Board + Probation Limits */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Left: Performance Metrics Card (2/3 width) */}
                  <div className="lg:col-span-2 bg-white rounded-xl p-5 border border-slate-200/90 shadow-xs flex flex-col justify-between">
                    <div>
                      {/* Subheader: Filter Dropdown + Timestamp */}
                      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <select 
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value as any)}
                            className="text-xs font-bold text-slate-800 bg-slate-100/90 hover:bg-slate-200 border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none cursor-pointer"
                          >
                            <option value="today">วันนี้</option>
                            <option value="7days">7 วันที่ผ่านมา</option>
                            <option value="30days">30 วันที่ผ่านมา</option>
                          </select>
                        </div>

                        <div className="flex items-center gap-2 text-[11px] text-slate-400">
                          <span>อัปเดตเมื่อ {lastUpdatedTime} (GMT+07:00)</span>
                          <button 
                            onClick={fetchDashboardData}
                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                            title="รีเฟรชข้อมูลตัวชี้วัด"
                          >
                            <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                          </button>
                        </div>
                      </div>

                      {/* 4 Metrics Columns */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-5">
                        {/* 1. GMV */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-bold text-slate-600">
                            <span>GMV</span>
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                          </div>
                          <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                            ฿{gmvTotal.toLocaleString()}
                          </div>
                          <div className="text-[10.5px] text-slate-400">
                            ยอดขายรวมคำสั่งซื้อ
                          </div>
                        </div>

                        {/* 2. ลูกค้า */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-bold text-slate-600">
                            <span>ลูกค้า</span>
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                          </div>
                          <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                            {uniqueCustomers}
                          </div>
                          <div className="text-[10.5px] text-slate-400">
                            ผู้ซื้อทั้งหมด
                          </div>
                        </div>

                        {/* 3. คำสั่งซื้อ SKU */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-bold text-slate-600">
                            <span>คำสั่งซื้อ SKU</span>
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                          </div>
                          <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                            {skuOrdersCount}
                          </div>
                          <div className="text-[10.5px] text-slate-400">
                            จำนวนชิ้นที่จำหน่าย
                          </div>
                        </div>

                        {/* 4. ผู้เข้าชม */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-bold text-slate-600">
                            <span>ผู้เข้าชม</span>
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                              {visitorsCount}
                            </span>
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-600">
                              <ArrowUp className="w-2.5 h-2.5" /> 200%
                            </span>
                          </div>
                          <div className="text-[10.5px] text-slate-400">
                            เข้าชมร้านค้า
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: ช่วงปรับตัวของร้านค้า (1/3 width) */}
                  <div className="bg-white rounded-xl p-5 border border-slate-200/90 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <h3 className="text-xs font-black text-slate-900">
                          ช่วงปรับตัวของร้านค้า
                        </h3>
                        <div className="flex items-center gap-1">
                          <button className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700">
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <button className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700">
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
                        ทำเป้าหมายทั้งหมดให้สำเร็จเพื่อยกเลิกการจำกัดร้านค้าและเข้าถึงสิทธิประโยชน์เต็มรูปแบบ
                      </p>

                      <div className="space-y-3">
                        {/* Daily order limit */}
                        <div>
                          <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1">
                            <span className="flex items-center gap-1">
                              <Lock className="w-3 h-3 text-slate-400" />
                              ขีดจำกัดคำสั่งซื้อรายวัน
                            </span>
                            <span className="font-mono text-slate-600">{orders.length}/100</span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-teal-500 rounded-full transition-all"
                              style={{ width: `${Math.min(100, (orders.length / 100) * 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Product item limit */}
                        <div>
                          <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1">
                            <span className="flex items-center gap-1">
                              <Lock className="w-3 h-3 text-slate-400" />
                              ขีดจำกัดรายการสินค้าทั้งหมด
                            </span>
                            <span className="font-mono text-slate-600">
                              {availableProducts.length > 0 ? availableProducts.length : 50}/1000
                            </span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-teal-500 rounded-full transition-all"
                              style={{ width: `${Math.min(100, ((availableProducts.length || 50) / 1000) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* RECENT ORDERS SUMMARY TABLE ON HOME TAB */}
                <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-[#2A59FE]" />
                      <h3 className="text-xs sm:text-sm font-black text-slate-900">
                        รายการสั่งซื้อล่าสุด ({orders.length} ออเดอร์)
                      </h3>
                    </div>
                    <button
                      onClick={() => setActiveTab('orders')}
                      className="text-xs font-bold text-[#2A59FE] hover:text-[#1E3A8A] flex items-center gap-1"
                    >
                      <span>ดูคำสั่งซื้อทั้งหมด</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-100 text-[11px]">
                        <tr>
                          <th className="p-3.5">ID คำสั่งซื้อ</th>
                          <th className="p-3.5">ลูกค้า</th>
                          <th className="p-3.5">ยอดรวม</th>
                          <th className="p-3.5">วิธีชำระ</th>
                          <th className="p-3.5">สถานะ</th>
                          <th className="p-3.5 text-right">การจัดการ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {orders.slice(0, 5).map((o) => (
                          <tr key={o.orderId} className="hover:bg-slate-50/60 transition-colors">
                            <td className="p-3.5 font-mono font-bold text-[#2A59FE]">
                              {o.orderId}
                            </td>
                            <td className="p-3.5">
                              <div className="font-bold text-slate-800">{o.customerName}</div>
                              <div className="text-[10px] text-slate-400 font-mono">{o.phone}</div>
                            </td>
                            <td className="p-3.5 font-black text-slate-900">
                              ฿{o.totalAmount.toLocaleString()}
                            </td>
                            <td className="p-3.5 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                o.paymentMethod === 'BANK_TRANSFER'
                                  ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                  : 'bg-amber-50 text-amber-700 border border-amber-100'
                              }`}>
                                {o.paymentMethod}
                              </span>
                            </td>
                            <td className="p-3.5 whitespace-nowrap">
                              <select
                                value={o.orderStatus}
                                onChange={(e) => handleUpdateStatus(o.orderId, e.target.value as OrderStatus)}
                                disabled={updatingId === o.orderId}
                                className="text-[11px] bg-slate-50 border border-slate-200 rounded-md px-2 py-1 font-bold text-slate-700 focus:outline-none cursor-pointer"
                              >
                                <option value="Wait_Verification">Wait_Verification</option>
                                <option value="Pending_Shipment">Pending_Shipment</option>
                                <option value="Processing">Processing</option>
                                <option value="Shipped">Shipped</option>
                                <option value="Completed">Completed</option>
                              </select>
                            </td>
                            <td className="p-3.5 text-right whitespace-nowrap">
                              <button
                                onClick={() => onSelectOrderForSlip(o)}
                                className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-[#2A59FE] font-bold rounded-lg text-xs transition-colors"
                              >
                                เปิดสลิป
                              </button>
                            </td>
                          </tr>
                        ))}
                        {orders.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">
                              ยังไม่มีรายการสั่งซื้อในระบบขณะนี้
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: PAYMENT SETTINGS (ระบบชำระค่าสินค้า & เชื่อมต่อ QR Payment) */}
          {activeTab === 'payments' && (
            <div className="p-4 sm:p-6 space-y-5">
              {/* Header Navigation for Payment Sub-tabs */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 sm:p-5">
                <div className="border-b border-slate-100 pb-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#2A59FE] shrink-0" />
                      <h2 className="text-base font-bold text-slate-900">ระบบชำระค่าสินค้า</h2>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      ตั้งค่าและเลือกช่องทางรับชำระเงิน พร้อมระบบเชื่อมต่อคิวอาร์เพย์เม้นท์ (QR Payment Architecture) แบบไร้รอยต่อ
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200/80 text-xs font-bold shrink-0">
                    <button
                      onClick={() => setPaymentSubTab('channels')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                        paymentSubTab === 'channels'
                          ? 'bg-white text-[#2A59FE] font-black shadow-2xs border border-blue-200/80'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>1. ช่องทางรับชำระเงิน</span>
                    </button>
                    <button
                      onClick={() => setPaymentSubTab('qr_integration')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                        paymentSubTab === 'qr_integration'
                          ? 'bg-white text-[#2A59FE] font-black shadow-2xs border border-blue-200/80'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Smartphone className="w-3.5 h-3.5 text-[#2A59FE]" />
                      <span>2. เชื่อมต่อ คิวอาร์เพย์เม้นท์</span>
                      <span className="text-[9px] font-black px-1.5 py-0.2 rounded-full bg-blue-100 text-[#2A59FE]">
                        EMVCo
                      </span>
                    </button>
                  </div>
                </div>

                {/* Sub-view: Channels Overview */}
                {paymentSubTab === 'channels' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-slate-500 font-medium">
                        เลือกเปิด-ปิดช่องทางที่ต้องการให้แสดงในหน้าชำระเงิน (Checkout)
                      </span>
                      <button
                        onClick={handleSavePayments}
                        className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 active:scale-95 rounded-lg px-4 py-2 transition-all shadow-2xs cursor-pointer"
                        title="บันทึกการตั้งค่าทั้งหมด"
                      >
                        <Save className="w-3.5 h-3.5" />
                        <span>บันทึกการใช้งาน</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        {
                          key: 'cod',
                          title: 'เก็บเงินปลายทาง (Cash on Delivery)',
                          description: 'เปิดรับชำระเงินสดที่ปลายทางเมื่อขนส่งส่งมอบสินค้าถึงมือผู้รับ ช่วยเพิ่มความมั่นใจให้ผู้ซื้อสินค้า',
                          icon: '💵',
                          badge: 'COD'
                        },
                        {
                          key: 'qr',
                          title: 'คิวอาร์เพย์เม้นท์ (QR Payment)',
                          description: 'รองรับการสแกนสลิปชำระเงินผ่านระบบ QR Code พร้อมเพย์ สะดวกรวดเร็ว ตรวจสอบยอดได้ทันที',
                          icon: '📱',
                          badge: 'PromptPay'
                        },
                        {
                          key: 'card',
                          title: 'บัตรเครดิต/บัตรเดบิต (Credit/Debit Card)',
                          description: 'รองรับบัตรเครดิตและเดบิตชั้นนำทุกธนาคาร เช่น Visa, Mastercard และ JCB เพิ่มความหลากหลายในการรับเงิน',
                          icon: '💳',
                          badge: 'Secure Card'
                        }
                      ].map((method) => {
                        const isSelected = tempPaymentMethods[method.key as 'cod' | 'qr' | 'card'];
                        return (
                          <div
                            key={method.key}
                            onClick={() => {
                              setTempPaymentMethods(prev => ({
                                ...prev,
                                [method.key]: !prev[method.key as 'cod' | 'qr' | 'card']
                              }));
                            }}
                            className={`border rounded-xl p-4 transition-all cursor-pointer select-none relative flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#3B66FE] bg-blue-50/30 text-slate-900 shadow-2xs font-bold'
                                : 'border-slate-200 hover:border-slate-300 bg-white text-slate-500'
                            }`}
                          >
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-3">
                                <span className="text-2xl">{method.icon}</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  isSelected
                                    ? 'bg-blue-100 text-[#2A59FE] border border-blue-200'
                                    : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {method.badge}
                                </span>
                              </div>

                              <h3 className="text-xs sm:text-sm font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
                                {method.title}
                              </h3>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                {method.description}
                              </p>
                            </div>

                            <div className="mt-4 pt-3 border-t border-slate-100/80 flex items-center justify-between">
                              <span className={`text-xs font-bold ${isSelected ? 'text-[#2A59FE]' : 'text-slate-400'}`}>
                                {isSelected ? 'เปิดใช้งานอยู่' : 'ปิดใช้งาน'}
                              </span>
                              <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                                isSelected
                                  ? 'bg-[#2A59FE] border-[#2A59FE] text-white'
                                  : 'border-slate-300 bg-white'
                              }`}>
                                {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Quick Link Card to QR Configuration */}
                    <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 via-blue-50/50 to-indigo-50/40 border border-blue-200/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white flex items-center justify-center shrink-0 shadow-xs">
                          <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900">
                            ต้องการตั้งค่า Biller ID, Webhook หรือทดสอบ Dynamic QR Code หรือไม่?
                          </div>
                          <div className="text-[11px] text-slate-500">
                            เข้าสู่เมนู "เชื่อมต่อ คิวอาร์เพย์เม้นท์" เพื่อจัดการสถาปัตยกรรมชำระเงิน EMVCo, ระบบตรวจสอบสลิปซ้ำ และสมุดบัญชีแยกประเภทคู่
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setPaymentSubTab('qr_integration')}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#0E2C58] hover:bg-[#1E3A8A] px-4 py-2 rounded-xl transition-all shadow-2xs cursor-pointer shrink-0"
                      >
                        <span>เปิดระบบเชื่อมต่อ QR Payment</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* สถาปัตยกรรม: รายการลงบัญชีรายวันล่าสุด (Recent Journal Entries) ย้ายมาไว้ด้านหน้า ตำแหน่งด้านล่าง การตั้งค่า ตามภาพเพื่อความสะดวกในการใช้งาน */}
                    <div className="mt-5">
                      <RecentJournalEntriesTable onOpenFullLedger={() => setPaymentSubTab('qr_integration')} />
                    </div>
                  </div>
                )}
              </div>

              {/* Sub-view: QR Payment Integration Architecture Panel */}
              {paymentSubTab === 'qr_integration' && (
                <QRPaymentConfigPanel />
              )}
            </div>
          )}

          {/* VIEW: LOGISTICS ADDRESSES (เมื่อกดปุ่ม โลจิสติก จะเห็นเพียงดีไซน์ตามภาพเท่านั้น พร้อมปุ่มแก้ไขและบันทึกข้อมูล) */}
          {activeTab === 'logistics' && (
            <div className="p-4 sm:p-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  {/* คอลัมน์ที่ 1: ที่อยู่เข้ารับพัสดุ และ บริษัทขนส่ง */}
                  <div className="flex flex-col gap-4">
                    {/* ตารางที่ 1: ที่อยู่เข้ารับพัสดุ */}
                    <div className="border border-blue-200/80 rounded-xl overflow-hidden bg-white shadow-2xs flex flex-col justify-between">
                      <div>
                        <div className="bg-[#eff6ff]/80 border-b border-blue-100 px-4 py-2.5 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-[#2A59FE] shrink-0" />
                            <span className="text-xs sm:text-sm font-bold text-slate-900">ที่อยู่เข้ารับพัสดุ</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-[#2A59FE] bg-blue-50 border border-blue-200/80 px-2.5 py-0.5 rounded-full">
                              จุดรับพัสดุ
                            </span>
                            {!isEditingPickup ? (
                              <button
                                onClick={() => {
                                  setTempPickup({ ...pickupAddress });
                                  setIsEditingPickup(true);
                                }}
                                className="inline-flex items-center gap-1 text-xs font-bold text-[#2A59FE] hover:text-[#1E3A8A] bg-white hover:bg-blue-50 border border-blue-300 rounded-lg px-2.5 py-1 transition-all shadow-2xs cursor-pointer"
                                title="แก้ไขที่อยู่เข้ารับพัสดุ"
                              >
                                <Edit2 className="w-3 h-3" />
                                <span>แก้ไข</span>
                              </button>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={handleSavePickup}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#2A59FE] hover:bg-[#1E3A8A] rounded-lg px-2.5 py-1 transition-all shadow-2xs cursor-pointer"
                                  title="บันทึกข้อมูล"
                                >
                                  <Save className="w-3 h-3" />
                                  <span>บันทึก</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setTempPickup({ ...pickupAddress });
                                    setIsEditingPickup(false);
                                  }}
                                  className="inline-flex items-center text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg px-2 py-1 transition-all cursor-pointer"
                                  title="ยกเลิก"
                                >
                                  <span>ยกเลิก</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="p-4 text-xs text-slate-800 space-y-1.5 font-medium leading-relaxed">
                          {!isEditingPickup ? (
                            <>
                              <div className="font-bold text-slate-900 text-sm">{pickupAddress.title}</div>
                              <div>{pickupAddress.address1}</div>
                              <div>{pickupAddress.address2}</div>
                              <div className="font-mono font-bold text-slate-700">{pickupAddress.postalCode}</div>
                            </>
                          ) : (
                            <div className="space-y-2 py-1">
                              <div>
                                <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ชื่อจุดรับ/ชื่อบริษัท</label>
                                <input
                                  type="text"
                                  value={tempPickup.title}
                                  onChange={(e) => setTempPickup({ ...tempPickup, title: e.target.value })}
                                  className="w-full text-xs font-bold text-slate-900 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-[#2A59FE] bg-slate-50 focus:bg-white transition-colors"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ที่อยู่บรรทัดที่ 1 (เลขที่, ซอย, ถนน)</label>
                                <input
                                  type="text"
                                  value={tempPickup.address1}
                                  onChange={(e) => setTempPickup({ ...tempPickup, address1: e.target.value })}
                                  className="w-full text-xs text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-[#2A59FE] bg-slate-50 focus:bg-white transition-colors"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ที่อยู่บรรทัดที่ 2 (แขวง/ตำบล, เขต/อำเภอ, จังหวัด)</label>
                                <input
                                  type="text"
                                  value={tempPickup.address2}
                                  onChange={(e) => setTempPickup({ ...tempPickup, address2: e.target.value })}
                                  className="w-full text-xs text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-[#2A59FE] bg-slate-50 focus:bg-white transition-colors"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] font-bold text-slate-600 block mb-0.5">รหัสไปรษณีย์</label>
                                <input
                                  type="text"
                                  value={tempPickup.postalCode}
                                  onChange={(e) => setTempPickup({ ...tempPickup, postalCode: e.target.value })}
                                  className="w-32 font-mono text-xs font-bold text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-[#2A59FE] bg-slate-50 focus:bg-white transition-colors"
                                />
                              </div>
                            </div>
                          )}

                          <div className="pt-2.5 mt-2 border-t border-slate-100 flex items-center gap-1.5 text-slate-600">
                            <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="text-slate-600 font-medium text-xs">โทรศัพท์:</span>
                            {!isEditingPickup ? (
                              <span className="font-mono font-bold text-slate-900 text-xs">{pickupAddress.phone}</span>
                            ) : (
                              <input
                                type="text"
                                value={tempPickup.phone}
                                onChange={(e) => setTempPickup({ ...tempPickup, phone: e.target.value })}
                                className="font-mono text-xs font-bold text-slate-900 px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:border-[#2A59FE] bg-slate-50 focus:bg-white transition-colors w-36"
                                placeholder="0939678649"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ตารางที่ 3: บริษัทขนส่งที่เปิดให้บริการในประเทศไทย ทั้งหมด */}
                    <div className="border border-blue-200 rounded-xl overflow-hidden bg-white shadow-2xs flex flex-col justify-between">
                      <div>
                        <div className="bg-[#eff6ff] border-b border-blue-100 px-4 py-2.5 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-[#2A59FE] shrink-0" />
                            <span className="text-xs sm:text-sm font-bold text-slate-900">ผู้ให้บริการขนส่งที่เปิดให้บริการ</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-[#2A59FE] bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full hidden sm:inline-block">
                              ทั้งหมดในไทย
                            </span>
                            <button
                              onClick={handleSaveCourier}
                              className="inline-flex items-center gap-1 text-xs font-bold text-white bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] hover:opacity-95 rounded-lg px-2.5 py-1 transition-all shadow-2xs cursor-pointer"
                              title="บันทึกผู้ให้บริการขนส่ง"
                            >
                              <Save className="w-3 h-3" />
                              <span>บันทึก</span>
                            </button>
                          </div>
                        </div>

                        <div className="p-4 text-xs text-slate-800 font-medium">
                          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2.5">
                            เลือกผู้ให้บริการขนส่งสำหรับที่อยู่เข้ารับพัสดุนี้:
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[
                              { id: 'thailand-post', name: 'ไปรษณีย์ไทย (Thailand Post)', logo: '📮' },
                              { id: 'kerry', name: 'Kerry Express / KEX', logo: '📦' },
                              { id: 'flash', name: 'Flash Express', logo: '⚡' },
                              { id: 'jt', name: 'J&T Express', logo: '🔴' },
                              { id: 'best', name: 'Best Express', logo: '🔵' },
                              { id: 'dhl', name: 'DHL Express', logo: '🟡' },
                              { id: 'ninjavan', name: 'Ninja Van', logo: '🥷' },
                              { id: 'spx', name: 'Shopee Xpress (SPX)', logo: '🟠' },
                              { id: 'scg', name: 'SCG Express', logo: '🧊' },
                              { id: 'deliveree', name: 'Deliveree', logo: '🟢' },
                              { id: 'lalamove', name: 'Lalamove', logo: '🧡' },
                            ].map((courier) => (
                              <label
                                key={courier.id}
                                className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all cursor-pointer select-none ${
                                  tempCourier === courier.id
                                    ? 'border-[#2A59FE] bg-blue-50/50 text-[#1E3A8A] shadow-3xs font-bold'
                                    : 'border-slate-100 hover:border-slate-300 bg-slate-50/50 text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="courier-selection"
                                  checked={tempCourier === courier.id}
                                  onChange={() => setTempCourier(courier.id)}
                                  className="w-4 h-4 text-[#2A59FE] border-slate-300 focus:ring-[#2A59FE] cursor-pointer"
                                />
                                <span className="text-base shrink-0">{courier.logo}</span>
                                <span className="truncate text-xs">{courier.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ตารางที่ 2: ที่อยู่ตีคืนสินค้า */}
                  <div className="border border-rose-200/80 rounded-xl overflow-hidden bg-white shadow-2xs flex flex-col justify-between">
                    <div>
                      <div className="bg-[#fff1f2]/80 border-b border-rose-100 px-4 py-2.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-rose-600 shrink-0" />
                          <span className="text-xs sm:text-sm font-bold text-slate-900">ที่อยู่ตีคืนสินค้า</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200/80 px-2.5 py-0.5 rounded-full">
                            คลังสินค้าตีคืน
                          </span>
                          {!isEditingReturn ? (
                            <button
                              onClick={() => {
                                setTempReturn({ ...returnAddress });
                                setIsEditingReturn(true);
                              }}
                              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 hover:text-rose-900 bg-white hover:bg-rose-50 border border-rose-300 rounded-lg px-2.5 py-1 transition-all shadow-2xs cursor-pointer"
                              title="แก้ไขที่อยู่ตีคืนสินค้า"
                            >
                              <Edit2 className="w-3 h-3" />
                              <span>แก้ไข</span>
                            </button>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={handleSaveReturn}
                                className="inline-flex items-center gap-1 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg px-2.5 py-1 transition-all shadow-2xs cursor-pointer"
                                title="บันทึกข้อมูล"
                              >
                                <Save className="w-3 h-3" />
                                <span>บันทึก</span>
                              </button>
                              <button
                                onClick={() => {
                                  setTempReturn({ ...returnAddress });
                                  setIsEditingReturn(false);
                                }}
                                className="inline-flex items-center text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg px-2 py-1 transition-all cursor-pointer"
                                title="ยกเลิก"
                              >
                                <span>ยกเลิก</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="p-4 text-xs text-slate-800 space-y-1.5 font-medium leading-relaxed">
                        {!isEditingReturn ? (
                          <>
                            <div className="font-bold text-slate-900 text-sm">{returnAddress.title}</div>
                            <div>{returnAddress.address1}</div>
                            <div>{returnAddress.address2}</div>
                            <div className="font-mono font-bold text-slate-700">{returnAddress.postalCode}</div>
                          </>
                        ) : (
                          <div className="space-y-2 py-1">
                            <div>
                              <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ชื่อคลังสินค้า/ผู้รับคืน</label>
                              <input
                                type="text"
                                value={tempReturn.title}
                                onChange={(e) => setTempReturn({ ...tempReturn, title: e.target.value })}
                                className="w-full text-xs font-bold text-slate-900 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-rose-500 bg-slate-50 focus:bg-white transition-colors"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ที่อยู่บรรทัดที่ 1 (เลขที่, ซอย, ถนน)</label>
                              <input
                                type="text"
                                value={tempReturn.address1}
                                onChange={(e) => setTempReturn({ ...tempReturn, address1: e.target.value })}
                                className="w-full text-xs text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-rose-500 bg-slate-50 focus:bg-white transition-colors"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] font-bold text-slate-600 block mb-0.5">ที่อยู่บรรทัดที่ 2 (แขวง/ตำบล, เขต/อำเภอ, จังหวัด)</label>
                              <input
                                type="text"
                                value={tempReturn.address2}
                                onChange={(e) => setTempReturn({ ...tempReturn, address2: e.target.value })}
                                className="w-full text-xs text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-rose-500 bg-slate-50 focus:bg-white transition-colors"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] font-bold text-slate-600 block mb-0.5">รหัสไปรษณีย์</label>
                              <input
                                type="text"
                                value={tempReturn.postalCode}
                                onChange={(e) => setTempReturn({ ...tempReturn, postalCode: e.target.value })}
                                className="w-32 font-mono text-xs font-bold text-slate-800 px-2.5 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:border-rose-500 bg-slate-50 focus:bg-white transition-colors"
                              />
                            </div>
                          </div>
                        )}

                        <div className="pt-2.5 mt-2 border-t border-slate-100 flex items-center gap-1.5 text-slate-600">
                          <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-slate-600 font-medium text-xs">โทรศัพท์:</span>
                          {!isEditingReturn ? (
                            <span className="font-mono font-bold text-slate-900 text-xs">{returnAddress.phone}</span>
                          ) : (
                            <input
                              type="text"
                              value={tempReturn.phone}
                              onChange={(e) => setTempReturn({ ...tempReturn, phone: e.target.value })}
                              className="font-mono text-xs font-bold text-slate-900 px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:border-rose-500 bg-slate-50 focus:bg-white transition-colors w-36"
                              placeholder="0939678649"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ตารางที่ 4: การตั้งค่าเชื่อมต่อระบบขนส่ง Flash Express Open API (v3) - รองรับ Environment Variables หลังบ้าน */}
                <div className="mt-5 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs transition-all">
                  <div 
                    onClick={() => setIsFlashConfigOpen(!isFlashConfigOpen)}
                    className="bg-slate-50/90 hover:bg-slate-100/80 px-4 py-3 flex items-center justify-between gap-3 cursor-pointer select-none transition-colors border-b border-transparent"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center font-black text-sm shadow-2xs">
                        ⚡
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs sm:text-sm font-black text-slate-800">การตั้งค่าเชื่อมต่อ Flash Express API</span>
                          {isFlashEnvConfigured ? (
                            <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              เชื่อมต่อผ่าน Environment Variables หลังบ้านแล้ว (ปลอดภัยสูงสุด)
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                              {flashMerchantId && flashSecretKey ? '● เชื่อมต่อแล้ว' : '● พร้อมเชื่อมต่อ'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {isFlashEnvConfigured 
                            ? `ระบบดึง Merchant ID (${flashMaskedMch || '••••'}) และ Secret Key จากระบบหลังบ้านโดยตรง ไม่ต้องกรอกหน้าบ้าน`
                            : (isFlashConfigOpen 
                              ? 'คลิกเพื่อซ่อนรายละเอียดการตั้งค่า' 
                              : 'ระบบดึงค่าจากระบบหลังบ้านอัตโนมัติ (คลิกเพื่อดูรายละเอียด)')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500 hidden sm:inline">
                        {isFlashConfigOpen ? 'ซ่อน' : 'แสดงการตั้งค่า'}
                      </span>
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 shadow-2xs">
                        {isFlashConfigOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                    </div>
                  </div>

                  {/* เนื้อหาการตั้งค่าจะถูกซ่อนไว้ แสดงเฉพาะเมื่อผู้ใช้ต้องการเปิดดูหรือแก้ไข */}
                  {isFlashConfigOpen && (
                    <div className="border-t border-slate-200 p-4 sm:p-5 space-y-4 text-xs bg-white">
                      {isFlashEnvConfigured ? (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-950 flex items-start gap-2.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            <p className="font-bold text-xs text-emerald-900">
                              ระบบเชื่อมต่อผ่าน Environment Variables หลังบ้านเรียบร้อยแล้ว
                            </p>
                            <p className="text-[11px] text-emerald-800 leading-relaxed">
                              คีย์ <code className="bg-emerald-100 px-1.5 py-0.5 rounded font-mono font-bold text-[10px]">FLASH_EXPRESS_MERCHANT_ID</code> และ <code className="bg-emerald-100 px-1.5 py-0.5 rounded font-mono font-bold text-[10px]">FLASH_EXPRESS_SECRET_KEY</code> ถูกโหลดและรักษาความปลอดภัยอยู่ที่ Server-side โดยสมบูรณ์ ไม่มีการเปิดเผยคีย์สู่หน้าบ้าน ทำให้แอดมินสามารถกดปุ่ม "พร้อมจัดส่ง" เพื่อออกเลขติดตามพัสดุได้ทันทีโดยไม่ต้องกรอกรหัสใด ๆ หน้าบ้าน
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                          <span className="text-[11px] text-slate-500 font-medium">
                            ตั้งค่า Environment Variables หลังบ้าน หรือบันทึกค่าสำรองได้ที่นี่
                          </span>
                          <button
                            type="button"
                            id="btn-save-flash-credentials"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveFlashConfig();
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 active:scale-95 rounded-lg px-3 py-1.5 transition-all shadow-2xs cursor-pointer"
                            title="บันทึกการตั้งค่า Flash Express API"
                          >
                            <Save className="w-3.5 h-3.5" />
                            <span>บันทึกการเชื่อมต่อ Flash</span>
                          </button>
                        </div>
                      )}

                      {/* ข้อมูล Endpoint ปลายทาง */}
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-slate-700">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900 text-[11px]">API Endpoint ปลายทาง:</span>
                          <code className="font-mono text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 break-all select-all">
                            https://open-api.flashexpress.com/open/v3/orders
                          </code>
                        </div>
                        <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full w-fit">
                          ● พร้อมเชื่อมต่อ
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* ช่องกรอก Merchant ID */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                            <Key className="w-3.5 h-3.5 text-amber-600" />
                            <span>Merchant ID (รหัสร้านค้า Flash Express)</span>
                            {isFlashEnvConfigured && <span className="text-emerald-600 text-[10px] font-normal">(ใช้ค่าจากหลังบ้าน)</span>}
                          </label>
                          <input
                            type="text"
                            id="input-flash-merchant-id"
                            value={isFlashEnvConfigured ? (tempFlashMerchantId || flashMaskedMch || '••••••••') : tempFlashMerchantId}
                            onChange={(e) => setTempFlashMerchantId(e.target.value)}
                            disabled={isFlashEnvConfigured}
                            placeholder="เช่น A1234567 หรือ รหัสลูกค้าองค์กร Flash"
                            className={`w-full text-xs font-mono font-medium text-slate-900 px-3 py-2 border rounded-lg transition-colors placeholder:text-slate-400 placeholder:font-sans ${
                              isFlashEnvConfigured 
                                ? 'bg-slate-100 text-slate-600 border-slate-200 cursor-not-allowed' 
                                : 'bg-slate-50 focus:bg-white border-slate-300 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500'
                            }`}
                          />
                          <p className="text-[10px] text-slate-500">
                            {isFlashEnvConfigured ? 'ป้องกันความปลอดภัย: ค่านี้ถูกโหลดจากระบบ Environment Variables หลังบ้าน' : 'รับได้จากบัญชีธุรกิจ Flash Express Open Platform (mchId)'}
                          </p>
                        </div>

                        {/* ช่องกรอก Secret Key */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                            <Shield className="w-3.5 h-3.5 text-amber-600" />
                            <span>Secret Key (คีย์ความปลอดภัย)</span>
                            {isFlashEnvConfigured && <span className="text-emerald-600 text-[10px] font-normal">(ปลอดภัยในระบบหลังบ้าน)</span>}
                          </label>
                          <div className="relative">
                            <input
                              type={showFlashSecret ? 'text' : 'password'}
                              id="input-flash-secret-key"
                              value={isFlashEnvConfigured ? '••••••••••••••••••••••••••••••••' : tempFlashSecretKey}
                              onChange={(e) => setTempFlashSecretKey(e.target.value)}
                              disabled={isFlashEnvConfigured}
                              placeholder="กรอก Secret Key จาก Flash Express"
                              className={`w-full text-xs font-mono font-medium text-slate-900 pl-3 pr-10 py-2 border rounded-lg transition-colors placeholder:text-slate-400 placeholder:font-sans ${
                                isFlashEnvConfigured 
                                  ? 'bg-slate-100 text-slate-600 border-slate-200 cursor-not-allowed' 
                                  : 'bg-slate-50 focus:bg-white border-slate-300 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500'
                              }`}
                            />
                            {!isFlashEnvConfigured && (
                              <button
                                type="button"
                                onClick={() => setShowFlashSecret(!showFlashSecret)}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                                title={showFlashSecret ? 'ซ่อนคีย์' : 'แสดงคีย์'}
                              >
                                {showFlashSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-500">
                            {isFlashEnvConfigured ? 'ป้องกันความปลอดภัย: ใช้ประมวลผล SHA-256 ฝั่ง Server-side เท่านั้น' : 'ใช้สำหรับการสร้าง Digital Signature (SHA-256) ป้องกันการปลอมแปลง'}
                          </p>
                        </div>
                      </div>

                      {/* กล่องสรุปการจับคู่ข้อมูลที่อยู่ */}
                      <div className="p-3 bg-amber-50/50 border border-amber-200/70 rounded-lg text-slate-700 text-[11px] leading-relaxed">
                        <div className="font-bold text-amber-950 mb-1 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />
                          <span>การจับคู่ที่อยู่ส่งพัสดุ (Address Mapping):</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5 text-slate-600">
                          <div>
                            • <span className="font-bold text-slate-800">ที่อยู่เข้ารับ (Sender/src):</span> {pickupAddress.title} ({pickupAddress.postalCode})
                          </div>
                          <div>
                            • <span className="font-bold text-slate-800">ที่อยู่ตีคืน (Return):</span> {returnAddress.title} ({returnAddress.postalCode})
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* VIEW: 2. ORDERS MANAGEMENT (คำสั่งซื้อ) */}
          {activeTab === 'orders' && (
            <div className="p-4 sm:p-6 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50">
                  <div>
                    <h3 className="text-sm font-black text-slate-900">
                      คำสั่งซื้อทั้งหมด
                    </h3>
                    <p className="text-xs text-slate-500">ตรวจสอบสถานะการชำระเงิน และพิมพ์ใบเสร็จรับเงิน</p>
                  </div>

                  {/* Status filter dropdown & Refresh */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fetchDashboardData()}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold border border-slate-200 rounded-lg text-xs transition-colors shadow-2xs cursor-pointer active:scale-95"
                      title="รีเฟรชข้อมูลคำสั่งซื้อล่าสุด"
                    >
                      <RotateCw className={`w-3.5 h-3.5 text-teal-600 ${loading ? 'animate-spin' : ''}`} />
                      <span className="hidden sm:inline">รีเฟรช</span>
                    </button>
                    <span className="text-xs text-slate-500 font-medium">สถานะ:</span>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-1.5 font-bold text-slate-700 focus:outline-none cursor-pointer"
                    >
                      <option value="all">ทั้งหมด ({orders.length})</option>
                      <option value="pending">รอดำเนินการ ({countPending})</option>
                      <option value="pending_shipment">รอจัดส่ง ({countPendingShipment})</option>
                      <option value="shipping">ระหว่างจัดส่ง ({countShipped})</option>
                      <option value="completed">สำเร็จ ({countCompleted})</option>
                      <option value="returned">พัสดุตีคืน ({countReturned})</option>
                    </select>
                  </div>
                </div>

                {/* แถบปุ่ม Button สำหรับตรรกะการทำงาน (รอดำเนินการ, รอจัดส่ง, ระหว่างจัดส่ง, สำเร็จ, พัสดุตีคืน) */}
                <div className="px-4 py-3 bg-white border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* ทั้งหมด */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('all')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'all'
                          ? 'bg-slate-900 text-white shadow-xs'
                          : 'bg-slate-100 hover:bg-slate-200/80 text-slate-700 border border-slate-200/70'
                      }`}
                    >
                      <span>ทั้งหมด</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {countAll}
                      </span>
                    </button>

                    {/* 1. รอดำเนินการ */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('pending')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'pending'
                          ? 'bg-amber-600 text-white shadow-xs'
                          : 'bg-amber-50 hover:bg-amber-100/80 text-amber-800 border border-amber-200'
                      }`}
                      title="คำสั่งซื้อที่รอดำเนินการ"
                    >
                      <Clock className="w-3.5 h-3.5" />
                      <span>รอดำเนินการ</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'pending' ? 'bg-white/20 text-white' : 'bg-amber-200 text-amber-900'
                      }`}>
                        {countPending}
                      </span>
                    </button>

                    {/* 2. รอจัดส่ง */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('pending_shipment')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'pending_shipment'
                          ? 'bg-teal-700 text-white shadow-xs'
                          : 'bg-teal-50 hover:bg-teal-100/80 text-teal-800 border border-teal-200'
                      }`}
                      title="คำสั่งซื้อที่รอจัดส่งพัสดุ"
                    >
                      <Package className="w-3.5 h-3.5" />
                      <span>รอจัดส่ง</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'pending_shipment' ? 'bg-white/20 text-white' : 'bg-teal-200 text-teal-900'
                      }`}>
                        {countPendingShipment}
                      </span>
                    </button>

                    {/* 3. ระหว่างจัดส่ง */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('shipping')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'shipping'
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-blue-50 hover:bg-blue-100/80 text-blue-800 border border-blue-200'
                      }`}
                      title="คำสั่งซื้อที่อยู่ระหว่างการจัดส่ง"
                    >
                      <Truck className="w-3.5 h-3.5" />
                      <span>ระหว่างจัดส่ง</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'shipping' ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-900'
                      }`}>
                        {countShipped}
                      </span>
                    </button>

                    {/* 4. สำเร็จ */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('completed')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'completed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-emerald-50 hover:bg-emerald-100/80 text-emerald-800 border border-emerald-200'
                      }`}
                      title="คำสั่งซื้อที่จัดส่งและชำระเงินสำเร็จสมบูรณ์"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>สำเร็จ</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'completed' ? 'bg-white/20 text-white' : 'bg-emerald-200 text-emerald-900'
                      }`}>
                        {countCompleted}
                      </span>
                    </button>

                    {/* 5. พัสดุตีคืน */}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('returned')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        statusFilter === 'returned'
                          ? 'bg-rose-600 text-white shadow-xs'
                          : 'bg-rose-50 hover:bg-rose-100/80 text-rose-800 border border-rose-200'
                      }`}
                      title="คำสั่งซื้อที่เป็นพัสดุตีคืน"
                    >
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>พัสดุตีคืน</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black ${
                        statusFilter === 'returned' ? 'bg-white/20 text-white' : 'bg-rose-200 text-rose-900'
                      }`}>
                        {countReturned}
                      </span>
                    </button>

                    {/* ปุ่ม ลบ (ลบคำสั่งซื้อที่เลือกถาวรทันทีและออกจากฐานข้อมูล) */}
                    <button
                      type="button"
                      onClick={handleDeleteSelectedOrders}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        selectedOrderIds.length > 0
                          ? 'bg-red-600 hover:bg-red-700 text-white shadow-xs'
                          : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60'
                      }`}
                      title={selectedOrderIds.length > 0 ? `ลบ ${selectedOrderIds.length} รายการที่เลือกออกจากระบบถาวรทันที` : 'กรุณาติ๊กช่องหน้ารายการสั่งซื้อเพื่อลบ'}
                      disabled={selectedOrderIds.length === 0}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>ลบ</span>
                      {selectedOrderIds.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono font-black bg-white/20 text-white">
                          {selectedOrderIds.length}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* แถบการจัดการกลุ่ม (Batch Action) สำหรับออเดอร์ที่ติ๊กเลือก */}
                    {selectedOrderIds.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 bg-teal-50/80 border border-teal-200 px-3 py-1.5 rounded-lg">
                        <span className="text-xs font-bold text-teal-900">
                          เลือก {selectedOrderIds.length} รายการ:
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleBatchUpdateStatus('Processing')}
                            className="px-2 py-1 text-[11px] font-bold bg-white hover:bg-amber-100 text-amber-900 border border-amber-300 rounded shadow-2xs cursor-pointer active:scale-95"
                            title="เปลี่ยนสถานะเป็น รอดำเนินการ"
                          >
                            รอดำเนินการ
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBatchUpdateStatus('Pending_Shipment')}
                            className="px-2 py-1 text-[11px] font-bold bg-white hover:bg-teal-100 text-teal-900 border border-teal-300 rounded shadow-2xs cursor-pointer active:scale-95"
                            title="เปลี่ยนสถานะเป็น รอจัดส่ง"
                          >
                            รอจัดส่ง
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBatchUpdateStatus('Shipped')}
                            className="px-2 py-1 text-[11px] font-bold bg-white hover:bg-blue-100 text-blue-900 border border-blue-300 rounded shadow-2xs cursor-pointer active:scale-95"
                            title="เปลี่ยนสถานะเป็น ระหว่างจัดส่ง"
                          >
                            ระหว่างจัดส่ง
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBatchUpdateStatus('Completed')}
                            className="px-2 py-1 text-[11px] font-bold bg-white hover:bg-emerald-100 text-emerald-900 border border-emerald-300 rounded shadow-2xs cursor-pointer active:scale-95"
                            title="เปลี่ยนสถานะเป็น สำเร็จ"
                          >
                            สำเร็จ
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBatchUpdateStatus('Returned')}
                            className="px-2 py-1 text-[11px] font-bold bg-white hover:bg-rose-100 text-rose-900 border border-rose-300 rounded shadow-2xs cursor-pointer active:scale-95"
                            title="เปลี่ยนสถานะเป็น พัสดุตีคืน"
                          >
                            พัสดุตีคืน
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ปุ่มสำหรับ พิมพ์ (ตำแหน่งด้านล่าง รีเฟรช เพื่อรอกำหนดวางตรรกะการทำงานภายหลัง) */}
                    <button
                      type="button"
                      id="btn-print-orders"
                      onClick={() => {
                        // เพื่อรอกำหนดวางตรรกะการทำงานภายหลังตามคำสั่ง
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-bold border border-slate-300 hover:border-slate-400 rounded-lg text-xs transition-all shadow-2xs cursor-pointer active:scale-95"
                      title="พิมพ์ (รอกำหนดตรรกะการทำงานภายหลัง)"
                    >
                      <Printer className="w-3.5 h-3.5 text-slate-600" />
                      <span>พิมพ์</span>
                    </button>

                    {/* ปุ่มสำหรับ พร้อมจัดส่ง (ตำแหน่งข้างปุ่ม พิมพ์ เชื่อมต่อ Flash Express ตามคำสั่ง) */}
                    <button
                      type="button"
                      id="btn-shipping-orders"
                      onClick={handleShipToFlash}
                      disabled={isShippingLoading}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer active:scale-95 ${
                        selectedOrderIds.length > 0
                          ? 'bg-amber-500 hover:bg-amber-600 text-white border border-amber-600'
                          : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 hover:border-slate-400'
                      } ${isShippingLoading ? 'opacity-60 cursor-wait' : ''}`}
                      title={
                        selectedOrderIds.length > 0
                          ? `ส่ง ${selectedOrderIds.length} รายการที่เลือกไป Flash Express ทันที`
                          : 'คลิกเพื่อส่งรายการคำสั่งซื้อที่เลือกไปยัง Flash Express'
                      }
                    >
                      <Truck className={`w-3.5 h-3.5 ${selectedOrderIds.length > 0 ? 'text-white' : 'text-slate-600'} ${isShippingLoading ? 'animate-bounce' : ''}`} />
                      <span>{isShippingLoading ? 'กำลังส่งข้อมูล...' : 'พร้อมจัดส่ง'}</span>
                      {selectedOrderIds.length > 0 && !isShippingLoading && (
                        <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono font-black bg-amber-700 text-white">
                          {selectedOrderIds.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-black text-[11px]">
                      <tr>
                        <th className="p-3.5 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={filteredOrders.length > 0 && selectedOrderIds.length === filteredOrders.length}
                            onChange={() => toggleSelectAll(filteredOrders.map(o => o.orderId))}
                            className="w-4 h-4 rounded border-slate-300 text-[#2A59FE] focus:ring-[#2A59FE] cursor-pointer accent-[#2A59FE]"
                            title="เลือกทั้งหมด"
                          />
                        </th>
                        <th className="p-3.5">ID คำสั่งซื้อ</th>
                        <th className="p-3.5">วันเวลา</th>
                        <th className="p-3.5">ลูกค้า</th>
                        <th className="p-3.5 min-w-[200px]">รายการสั่งซื้อ</th>
                        <th className="p-3.5">ยอดรวมสุทธิ</th>
                        <th className="p-3.5">วิธีชำระ</th>
                        <th className="p-3.5">สถานะออเดอร์</th>
                        <th className="p-3.5 min-w-[190px]">หมายเลขติดตามพัสดุ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredOrders.map((o) => (
                        <tr key={o.orderId} className={`hover:bg-slate-50/60 transition-colors ${selectedOrderIds.includes(o.orderId) ? 'bg-blue-50/50' : ''}`}>
                          <td className="p-3.5 w-10 text-center">
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.includes(o.orderId)}
                              onChange={() => toggleSelectOrder(o.orderId)}
                              className="w-4 h-4 rounded border-slate-300 text-[#2A59FE] focus:ring-[#2A59FE] cursor-pointer accent-[#2A59FE]"
                              title={`เลือกคำสั่งซื้อ ${o.orderId}`}
                            />
                          </td>
                          <td className="p-3.5 font-mono font-bold whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setSelectedOrderForDetail(o)}
                              className="inline-flex items-center gap-1 text-[#2A59FE] hover:text-[#1E3A8A] hover:underline cursor-pointer group text-left transition-colors font-mono font-bold"
                              title="คลิกเพื่อดูรายละเอียดทั้งหมดของคำสั่งซื้อนี้"
                            >
                              <span>{o.orderId}</span>
                              <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all text-[#2A59FE]" />
                            </button>
                          </td>
                          <td className="p-3.5 text-slate-500 whitespace-nowrap font-mono">
                            {formatThaiDateTime(o.timestamp)}
                          </td>
                          <td className="p-3.5 font-bold text-slate-800 whitespace-nowrap">
                            {o.customerName}
                          </td>
                          <td className="p-3.5 max-w-[240px]">
                            {o.items && o.items.length > 0 ? (
                              <div className="space-y-0.5">
                                <div 
                                  className="font-semibold text-slate-800 text-xs truncate" 
                                  title={o.items.map(it => `${it.productName || 'สินค้า'} (x${it.quantity || 1})`).join(', ')}
                                >
                                  {o.items[0].productName || 'สินค้า'}
                                  <span className="text-[#2A59FE] font-bold ml-1">×{o.items[0].quantity || 1}</span>
                                </div>
                                {o.items.length > 1 && (
                                  <span className="inline-block text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200/70">
                                    +{o.items.length - 1} รายการเพิ่มเติม
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs italic">1 รายการ</span>
                            )}
                          </td>
                          <td className="p-3.5 font-black text-slate-900 whitespace-nowrap">
                            ฿{o.totalAmount.toLocaleString()}
                          </td>
                          <td className="p-3.5 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-md text-[10.5px] font-bold ${
                              o.paymentMethod === 'BANK_TRANSFER' || (o.paymentMethod as string) === 'PROMPTPAY'
                                ? 'bg-amber-50 text-amber-800 border border-amber-200 font-mono'
                                : 'bg-blue-50 text-blue-800 border border-blue-200 font-mono'
                            }`}>
                              {o.paymentMethod === 'BANK_TRANSFER' ? 'PROMPTPAY' : o.paymentMethod}
                            </span>
                          </td>
                          <td className="p-3.5 whitespace-nowrap">
                            <select
                              value={o.orderStatus}
                              onChange={(e) => handleUpdateStatus(o.orderId, e.target.value as OrderStatus)}
                              disabled={updatingId === o.orderId}
                              className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-bold text-slate-800 focus:outline-none cursor-pointer"
                            >
                              <option value="Wait_Verification">Wait_Verification (รอตรวจสอบสลิป)</option>
                              <option value="Pending_Shipment">Pending_Shipment (รอจัดส่ง)</option>
                              <option value="Processing">Processing (รอดำเนินการ)</option>
                              <option value="Shipped">Shipped (ระหว่างจัดส่ง)</option>
                              <option value="Completed">Completed (สำเร็จ)</option>
                              <option value="Returned">Returned (พัสดุตีคืน)</option>
                            </select>
                          </td>
                          <td className="p-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="text"
                                placeholder="พิมพ์เลขพัสดุ..."
                                value={trackingInputs[o.orderId] !== undefined ? trackingInputs[o.orderId] : (o.trackingNumber || '')}
                                onChange={(e) => setTrackingInputs(prev => ({ ...prev, [o.orderId]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveTracking(o.orderId);
                                  }
                                }}
                                className="w-32 sm:w-36 text-xs font-mono font-bold px-2.5 py-1.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-[#2A59FE] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2A59FE] text-slate-800 placeholder:text-slate-400 placeholder:font-normal transition-colors"
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveTracking(o.orderId)}
                                disabled={savingTrackingId === o.orderId}
                                className="px-2.5 py-1.5 bg-[#2A59FE] hover:bg-[#1E3A8A] disabled:bg-slate-300 text-white font-bold rounded-lg text-xs transition-colors shadow-2xs flex items-center gap-1 shrink-0 cursor-pointer active:scale-95"
                                title="บันทึกหมายเลขติดตามพัสดุ"
                              >
                                {savingTrackingId === o.orderId ? (
                                  <RotateCw className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Save className="w-3.5 h-3.5" />
                                )}
                                <span className="hidden sm:inline text-[11px]">บันทึก</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredOrders.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-12 text-center text-slate-400 font-medium">
                            ไม่พบรายการคำสั่งซื้อตามตัวกรองที่เลือก
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: 2.5 PRODUCT INVENTORY (คลังสินค้า) */}
          {activeTab === 'products' && (
            <div className="p-4 sm:p-6 space-y-4">
              <ProductInventoryList 
                availableProducts={availableProducts} 
                onSelectForTransparent={(code) => {
                  setSelectedProductForTransparent(code);
                  setActiveTab('transparent');
                }}
              />
            </div>
          )}

          {/* VIEW: 3. TRANSPARENT ASSETS / PRODUCTS (คลังภาพไลฟ์) */}
          {activeTab === 'transparent' && (
            <div className="p-4 sm:p-6 space-y-4">
              <TransparentImageLibrary 
                availableProducts={availableProducts} 
                initialSelectedProductCode={selectedProductForTransparent}
              />
            </div>
          )}

          {/* VIEW: 3.5 CHAT & SHOP (AI AGENT CONFIGURATION & QUALITY AUDIT) */}
          {activeTab === 'chatandshop' && (
            <div className="p-4 sm:p-6 space-y-6">
              <ChatAndShopSettingsTab />
            </div>
          )}

          {/* VIEW: 4. ANALYTICS (การวิเคราะห์) */}
          {activeTab === 'analytics' && (
            <div className="p-4 sm:p-6 space-y-6">
              {/* Daily, Monthly, Yearly Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Daily */}
                <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายประจำวันนี้</span>
                    <span className="px-2 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-black">Today</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-1 tracking-tight">
                    ฿{analytics?.daily?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.daily?.count || 0} รายการ</strong>
                  </div>
                </div>

                {/* Monthly */}
                <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายประจำเดือนนี้</span>
                    <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-black">Month</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-1 tracking-tight">
                    ฿{analytics?.monthly?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.monthly?.count || 0} รายการ</strong>
                  </div>
                </div>

                {/* Yearly */}
                <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายรวมตลอดปี</span>
                    <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-black">Year</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-1 tracking-tight">
                    ฿{analytics?.yearly?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.yearly?.count || 0} รายการ</strong>
                  </div>
                </div>
              </div>

              {/* Target progress bars */}
              <div className="bg-white p-6 rounded-xl border border-slate-200/90 shadow-xs">
                <h3 className="text-sm font-black text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#2A59FE]" />
                  <span>เป้าหมายยอดขายและการเติบโต</span>
                </h3>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                      <span>เป้าหมายรายวัน (฿15,000)</span>
                      <span>{Math.min(100, Math.round(((analytics?.daily?.sales || 0) / 15000) * 100))}%</span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] rounded-full"
                        style={{ width: `${Math.min(100, ((analytics?.daily?.sales || 0) / 15000) * 100)}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                      <span>เป้าหมายรายเดือน (฿150,000)</span>
                      <span>{Math.min(100, Math.round(((analytics?.monthly?.sales || 0) / 150000) * 100))}%</span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#3B66FE] to-[#4F46E5] rounded-full"
                        style={{ width: `${Math.min(100, ((analytics?.monthly?.sales || 0) / 150000) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: 5. ROLES / ACCOUNT STATUS (สถานะบัญชี & สิทธิ์) */}
          {activeTab === 'roles' && (
            <div className="p-4 sm:p-6 space-y-6">
              {/* Role RBAC Architecture Guide */}
              <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-[#2A59FE]" />
                  <h3 className="text-sm font-black text-slate-900">
                    โครงสร้างกำหนดสิทธิ์ผู้ใช้งาน (User RBAC Architecture)
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200/70">
                    <div className="font-bold text-xs text-slate-800 mb-1">1. ลูกค้าสมาชิก (Customer)</div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      สั่งซื้อสินค้าราคาสมาชิก, สะสมคะแนน PV, ตรวจสอบสถานะและประวัติการสั่งซื้อ
                    </p>
                  </div>
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200/70">
                    <div className="font-bold text-xs text-slate-800 mb-1">2. ตัวแทนจำหน่าย (Seller)</div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      เข้าถึงรายงานยอดขายรวม, จัดการสถานะคำสั่งซื้อ และเปิดสลิปใบเสร็จ
                    </p>
                  </div>
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200/70">
                    <div className="font-bold text-xs text-slate-800 mb-1">3. ผู้ดูแลสูงสุด (Admin)</div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      ควบคุมสิทธิ์ผู้ใช้ทั้งหมดในฐานข้อมูล, จัดการตั้งค่าอีเมลแจ้งเตือน และระบบ SMTP
                    </p>
                  </div>
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-xs sm:text-sm font-black text-slate-900">
                    บัญชีผู้ใช้งานในระบบ ({usersList.length} บัญชี)
                  </h3>
                  <p className="text-xs text-slate-500">ปรับเปลี่ยนสิทธิ์ของสมาชิกในระบบ</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 text-[11px]">
                      <tr>
                        <th className="p-3.5">Customer ID</th>
                        <th className="p-3.5">อีเมล</th>
                        <th className="p-3.5">สิทธิ์ปัจจุบัน</th>
                        <th className="p-3.5 text-right">เปลี่ยนสิทธิ์</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {usersList.map((usr) => (
                        <tr key={usr.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="p-3.5 font-mono font-bold text-[#2A59FE]">
                            {usr.customerId || 'ยังไม่มี PIN'}
                          </td>
                          <td className="p-3.5 text-slate-800 font-medium">
                            {usr.email}
                          </td>
                          <td className="p-3.5">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              usr.role === 'admin' 
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' 
                                : usr.role === 'seller' 
                                  ? 'bg-amber-50 text-amber-700 border border-amber-100' 
                                  : 'bg-blue-50 text-[#2A59FE] border border-blue-100'
                            }`}>
                              {usr.role || 'customer'}
                            </span>
                          </td>
                          <td className="p-3.5 text-right">
                            <select
                              value={usr.role || 'customer'}
                              onChange={(e) => handleRoleChange(usr.id, e.target.value as UserRole)}
                              className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none cursor-pointer"
                            >
                              <option value="customer">Customer</option>
                              <option value="seller">Seller</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                      {usersList.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">
                            ยังไม่มีบัญชีผู้ใช้ในระบบ
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: 6. FINANCE & EMAIL ALERTS (การเงิน & รับแจ้งเตือน) */}
          {activeTab === 'settings' && (
            <div className="p-4 sm:p-6 space-y-6">
              {/* SMTP Architecture Status Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0E2C58] text-white flex items-center justify-center font-bold shadow-xs">
                      <Server className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                        <span>ระบบแจ้งเตือนอีเมลคำสั่งซื้อ Real-time (SMTP Gateway)</span>
                        {emailStatus?.isSmtpConfigured ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> เชื่อมต่อแล้ว
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                            <AlertCircle className="w-3 h-3" /> พร้อมเชื่อมต่อ SMTP
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        ส่งอีเมลแจ้งเตือนทุกครั้งที่มีการสั่งซื้อใหม่ตรงเข้ากล่องข้อความของผู้ดูแล
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSendTestEmail()}
                    disabled={testingEmail}
                    className="px-4 py-2 bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 shrink-0"
                  >
                    <Send className={`w-3.5 h-3.5 ${testingEmail ? 'animate-spin' : ''}`} />
                    <span>{testingEmail ? 'กำลังส่ง...' : 'ทดสอบส่งอีเมลแจ้งเตือน'}</span>
                  </button>
                </div>

                <div className="mt-4 p-3.5 bg-slate-50 rounded-lg text-xs text-slate-600">
                  <div className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                    <Inbox className="w-3.5 h-3.5 text-[#2A59FE]" />
                    <span>แนะนำการตั้งค่า Gmail App Password (SMTP_PASS):</span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-slate-600">
                    บัญชีแอดมิน <strong className="text-slate-800">giffarine108037654@gmail.com</strong> สามารถเพิ่ม App Password 16 หลัก ใน Settings &gt; Secrets ในชื่อ <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-[#2A59FE]">SMTP_PASS</code> เพื่อส่งตรงเข้ากล่องข้อความทันที
                  </p>
                </div>
              </div>

              {/* Recipient Management Form & List */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <form onSubmit={handleAddEmail} className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs space-y-3">
                  <h3 className="text-xs sm:text-sm font-black text-slate-900 border-b border-slate-100 pb-2">
                    เพิ่มอีเมลรับการแจ้งเตือน
                  </h3>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      อีเมลแอดมิน <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="admin@yourdomain.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#2A59FE] focus:outline-none text-xs"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2 bg-[#2A59FE] text-white font-bold text-xs rounded-lg flex items-center justify-center gap-1.5 hover:bg-[#1E3A8A] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>เพิ่มอีเมล</span>
                  </button>
                </form>

                <div className="md:col-span-2 bg-white p-5 rounded-xl border border-slate-200/90 shadow-xs">
                  <h3 className="text-xs sm:text-sm font-black text-slate-900 border-b border-slate-100 pb-2 mb-3">
                    รายชื่อผู้รับเมล์แจ้งเตือน ({adminEmails.length} อีเมล)
                  </h3>
                  {adminEmails.length === 0 ? (
                    <p className="text-xs text-slate-400 py-4 text-center">ยังไม่มีอีเมลในระบบ</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {adminEmails.map((email) => (
                        <div key={email} className="flex items-center justify-between py-2.5">
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-[#2A59FE]" />
                            <span className="text-xs font-bold text-slate-700">{email}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleSendTestEmail(email)}
                              disabled={testingEmail}
                              className="px-2 py-1 text-[10px] font-bold text-[#2A59FE] bg-blue-50 hover:bg-blue-100 rounded"
                            >
                              ทดสอบส่ง
                            </button>
                            <button
                              onClick={() => handleDeleteEmail(email)}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Customer & Order Details Popup Modal (ป๊อปอัพข้อมูลลูกค้าและรายละเอียดคำสั่งซื้อ) */}
      {selectedOrderForDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] text-white flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center font-bold shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-black text-white">ข้อมูลลูกค้าและคำสั่งซื้อ</h3>
                    <span className="font-mono font-bold text-xs px-2 py-0.5 rounded-md bg-white/20 text-white border border-white/20">
                      {selectedOrderForDetail.orderId}
                    </span>
                  </div>
                  <p className="text-xs text-blue-100 mt-0.5">
                    สั่งซื้อเมื่อ {formatThaiDateTime(selectedOrderForDetail.timestamp)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedOrderForDetail(null)}
                className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/80 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                title="ปิดหน้าต่าง"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-5 text-xs text-slate-700 divide-y divide-slate-100">
              {/* 1. ข้อมูลลูกค้า: ชื่อ, เบอร์โทร, ที่อยู่จัดส่ง */}
              <div className="space-y-3 pt-0">
                <h4 className="font-black text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-[#2A59FE]" />
                  ข้อมูลลูกค้าและการจัดส่ง
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 bg-slate-50/80 p-4 rounded-xl border border-slate-200/80">
                  {/* ชื่อ */}
                  <div>
                    <span className="text-slate-400 text-[11px] block font-medium">ชื่อลูกค้า</span>
                    <span className="font-bold text-slate-900 text-sm">{selectedOrderForDetail.customerName || 'ไม่ระบุชื่อ'}</span>
                  </div>

                  {/* เบอร์โทร */}
                  <div>
                    <span className="text-slate-400 text-[11px] block font-medium">เบอร์โทร</span>
                    {selectedOrderForDetail.phone ? (
                      <a
                        href={`tel:${selectedOrderForDetail.phone}`}
                        className="font-mono font-bold text-[#2A59FE] hover:underline inline-flex items-center gap-1.5 text-sm"
                      >
                        <Phone className="w-3.5 h-3.5 text-[#2A59FE]" />
                        <span>{selectedOrderForDetail.phone}</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 italic">ไม่ระบุเบอร์โทร</span>
                    )}
                  </div>

                  {/* ที่อยู่จัดส่ง */}
                  <div className="sm:col-span-2 pt-2 border-t border-slate-200/70">
                    <span className="text-slate-400 text-[11px] block font-medium flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-rose-500" />
                      ที่อยู่จัดส่ง
                    </span>
                    <p className="font-semibold text-slate-800 text-xs mt-1 leading-relaxed whitespace-pre-line">
                      {selectedOrderForDetail.address || 'ไม่ระบุที่อยู่จัดส่ง'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 1.5 สถานะออเดอร์ และ หมายเลขพัสดุ */}
              <div className="space-y-3 pt-4">
                <h4 className="font-black text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Truck className="w-4 h-4 text-[#2A59FE]" />
                  สถานะออเดอร์และการจัดส่งพัสดุ
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 bg-slate-50/80 p-4 rounded-xl border border-slate-200/80">
                  <div>
                    <span className="text-slate-400 text-[11px] block font-medium">สถานะออเดอร์</span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-[#2A59FE] border border-blue-200 mt-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#2A59FE]" />
                      {selectedOrderForDetail.orderStatus || 'Pending_Shipment'}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 text-[11px] block font-medium">หมายเลขติดตามพัสดุ (Tracking No.)</span>
                    {selectedOrderForDetail.trackingNumber ? (
                      <span className="font-mono font-black text-[#1E3A8A] bg-blue-100/70 px-2.5 py-1 rounded-md text-xs inline-block mt-1 border border-blue-200">
                        {selectedOrderForDetail.trackingNumber}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-xs mt-1 block">ยังไม่มีหมายเลขพัสดุ (รอออกพัสดุ)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 2. รายการสั่งซื้อ */}
              <div className="space-y-3 pt-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-black text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Package className="w-4 h-4 text-[#2A59FE]" />
                    รายการสั่งซื้อ ({selectedOrderForDetail.items?.length || 0} รายการ)
                  </h4>
                  {selectedOrderForDetail.totalPv > 0 && (
                    <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      รวม {selectedOrderForDetail.totalPv} PV
                    </span>
                  )}
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                  {selectedOrderForDetail.items && selectedOrderForDetail.items.length > 0 ? (
                    selectedOrderForDetail.items.map((item, idx) => {
                      const prod = availableProducts.find(p => p.code === item.productCode || p.id === item.productCode);
                      return (
                        <div key={idx} className="p-3 bg-white flex items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden font-bold text-slate-400 text-[10px]">
                              {prod?.image ? (
                                <img src={prod.image} alt={item.productName} className="w-full h-full object-cover" />
                              ) : (
                                <Package className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-800 text-xs truncate">{item.productName}</p>
                              <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                                รหัส {item.productCode} • ฿{item.pricePerUnit?.toLocaleString()} × {item.quantity} ชิ้น
                                {item.pv > 0 && ` (${item.pv * item.quantity} PV)`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-black text-slate-900 text-xs">
                              ฿{(item.subtotal || item.pricePerUnit * item.quantity).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-4 text-center text-slate-400 italic">ไม่มีรายการสินค้า</div>
                  )}
                </div>
              </div>

              {/* 3. ยอดเงิน และ วิธีชำระเงิน */}
              <div className="space-y-3 pt-4">
                <h4 className="font-black text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-[#2A59FE]" />
                  ยอดเงินและวิธีชำระเงิน
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* วิธีชำระเงิน */}
                  <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-200/80 space-y-2.5">
                    <span className="text-slate-400 text-[11px] block font-medium">วิธีชำระเงิน</span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${
                        selectedOrderForDetail.paymentMethod === 'BANK_TRANSFER'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {selectedOrderForDetail.paymentMethod === 'BANK_TRANSFER'
                          ? 'โอนเงินผ่านบัญชีธนาคาร'
                          : 'เก็บเงินปลายทาง (COD)'}
                      </span>
                    </div>

                    <div className="text-[11px] text-slate-600 pt-1">
                      <span>สถานะการชำระ: </span>
                      <strong className="text-slate-900 font-bold">{selectedOrderForDetail.paymentStatus || 'รอตรวจสอบ'}</strong>
                    </div>

                    {selectedOrderForDetail.slipUrl && (
                      <div className="pt-1.5 border-t border-slate-200/70">
                        <a
                          href={selectedOrderForDetail.slipUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2A59FE] hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          ดูสลิปหลักฐานการโอนเงิน
                        </a>
                      </div>
                    )}
                  </div>

                  {/* ยอดเงิน */}
                  <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-200/80 space-y-2 font-medium">
                    <span className="text-slate-400 text-[11px] block font-medium">สรุปยอดเงิน</span>
                    <div className="flex justify-between text-slate-600 text-[11px]">
                      <span>ยอดรวมค่าสินค้า</span>
                      <span className="font-mono font-bold text-slate-800">฿{(selectedOrderForDetail.subtotal || selectedOrderForDetail.totalAmount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-slate-600 text-[11px]">
                      <span>ค่าจัดส่ง</span>
                      <span className="font-mono font-bold text-slate-800">
                        {selectedOrderForDetail.deliveryFee ? `฿${selectedOrderForDetail.deliveryFee}` : 'ฟรี'}
                      </span>
                    </div>
                    <div className="border-t border-slate-200/80 pt-2 flex justify-between items-center text-slate-900">
                      <span className="font-bold text-xs">ยอดเงินรวมสุทธิ</span>
                      <span className="font-black font-mono text-base text-[#2A59FE]">
                        ฿{selectedOrderForDetail.totalAmount?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3.5 sm:p-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  const o = selectedOrderForDetail;
                  setSelectedOrderForDetail(null);
                  onSelectOrderForSlip(o);
                }}
                className="px-3.5 py-2 bg-blue-50 hover:bg-blue-100 text-[#1E3A8A] font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <FileText className="w-4 h-4 text-[#2A59FE]" />
                <span>เปิดสลิปใบเสร็จเต็มรูปแบบ</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedOrderForDetail(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assistant Modal Dialog */}
      {assistantModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl border border-slate-200 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#2A59FE]" />
                <h3 className="text-sm font-black text-slate-900">ผู้ช่วยจัดการร้านค้า (Seller Assistant)</h3>
              </div>
              <button onClick={() => setAssistantModalOpen(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="space-y-2 text-xs text-slate-600 leading-relaxed">
              <p>• <strong>ตรวจคำสั่งซื้อ:</strong> สามารถกดที่การ์ด "คำสั่งซื้อที่จะจัดส่ง" เพื่อเข้าสู่ตารางออเดอร์ทันที</p>
              <p>• <strong>พิมพ์สลิป:</strong> กดปุ่ม "เปิดสลิปใบเสร็จ" เพื่อเปิดใบเสร็จรับเงินแบบละเอียดและพิมพ์ให้ลูกค้า</p>
              <p>• <strong>ดาวน์โหลดรูปสินค้า:</strong> เลือกเมนู "สินค้า & คลังภาพ" เพื่อโหลดไฟล์ PNG โปร่งแสงความละเอียดสูง</p>
            </div>
            <button
              onClick={() => setAssistantModalOpen(false)}
              className="w-full py-2 bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] hover:opacity-95 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
            >
              รับทราบ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
